import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Typography,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Link,
    TextField,
    Grid,
    Tab,
    Tabs,
    Checkbox,
    FormControlLabel,
    IconButton,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import Tesseract from "tesseract.js";
import axios from 'axios';
import Nlayout from './Nlayout';
import { BrowserQRCodeReader } from '@zxing/browser';
import { FaCheck } from 'react-icons/fa6';
import { FaTelegramPlane } from 'react-icons/fa';
import { BiError } from "react-icons/bi";
import {
    FiCalendar,
    FiUser,
    FiMail,
    FiImage,
    FiCopy,
    FiFingerprint,
    FiCheckCircle,
    FiXCircle,
    FiEye,
    FiDownload,
    FiInfo,
    FiRefreshCw,
    FiFileText,
    FiTrash2,
    FiCheck
} from 'react-icons/fi';
import './AdminPanel.css';

// ── Componente para mostrar la captura con manejo de carga y error ──────────
const CapturaImagen = ({ src }) => {
    const [estado, setEstado] = React.useState('cargando'); // 'cargando' | 'ok' | 'error'

    React.useEffect(() => {
        setEstado('cargando');
    }, [src]);

    return (
        <div style={{ textAlign: 'center', minHeight: 80 }}>
            {estado === 'cargando' && (
                <div style={{ padding: '24px 0', color: '#888' }}>
                    <CircularProgress size={32} />
                    <p style={{ marginTop: 10, fontSize: 13 }}>Cargando imagen…</p>
                </div>
            )}
            {estado === 'error' && (
                <Alert severity="error" sx={{ mb: 1 }}>
                    ⚠️ No se pudo cargar la imagen. La imagen no existe en el VPS.
                    <br />
                    <a href={src} target="_blank" rel="noopener noreferrer" style={{ color: '#ef4444', fontWeight: 700 }}>
                        Intentar abrir directamente →
                    </a>
                </Alert>
            )}
            <img
                src={src}
                alt="Captura de pantalla"
                crossOrigin="anonymous"
                style={{
                    maxWidth: '100%',
                    height: 'auto',
                    display: estado === 'ok' ? 'block' : 'none',
                    margin: '0 auto',
                    borderRadius: 8,
                    boxShadow: '0 2px 16px rgba(0,0,0,0.25)',
                }}
                onLoad={() => setEstado('ok')}
                onError={() => {
                    // Intentar sin crossOrigin como fallback
                    setEstado('error');
                }}
            />
        </div>
    );
};
// ────────────────────────────────────────────────────────────────────────────

const AdminPanel = (props) => {
    const [solicitudes, setSolicitudes] = useState([]);
    const [tabValue, setTabValue] = useState(0); // 0 = 1xBet, 1 = BetWinner
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedScreenshot, setSelectedScreenshot] = useState(null);
    const [selectedSolicitudId, setSelectedSolicitudId] = useState(null);

    const [openBulkAcceptModal, setOpenBulkAcceptModal] = useState(false);
    const [bulkAcceptList, setBulkAcceptList] = useState([]);
    const [bulkProcessing, setBulkProcessing] = useState(false);

    const [verificacionIA, setVerificacionIA] = useState({});
    const [verificacionFecha, setVerificacionFecha] = useState({});
    const [verificacionXbetId, setVerificacionXbetId] = useState({});

    const [bnbNuevasSet, setBnbNuevasSet] = useState(new Set());
    const [solicitudesHistoricas, setSolicitudesHistoricas] = useState([]); // aceptadas/rechazadas del historico
    const [bnbAsociadosMap, setBnbAsociadosMap] = useState({});
    const [canjesBlacklistSet, setCanjesBlacklistSet] = useState(new Set());
    const [canjesMap, setCanjesMap] = useState({});

    const handleAbrirModalBulkAccept = () => {
        const pendientesTab = solicitudes
            .filter((s) => s.status === 'pending')
            .filter(filterSolicitud);
        setBulkAcceptList(pendientesTab);
        setOpenBulkAcceptModal(true);
    };

    const handleRemoveFromBulkList = (id) => {
        setBulkAcceptList((prev) => prev.filter((s) => s.id !== id));
    };

    const handleConfirmBulkAccept = async () => {
        if (bulkAcceptList.length === 0) return;
        setBulkProcessing(true);
        setError('');
        const idsToAccept = bulkAcceptList.map((sol) => sol.id);
        try {
            await Promise.allSettled(
                bulkAcceptList.map((sol) =>
                    axios.put(
                        `https://backend.eldenguee.com/solicitudes/${sol.id}/update`,
                        { status: 'accepted', razon: '', nombre: sol.nombre, tipo: sol.tipo },
                        {
                            headers: {
                                Authorization: `Bearer ${localStorage.getItem('token')}`,
                            },
                        }
                    )
                )
            );
            setSolicitudes((prev) =>
                prev.map((solicitud) =>
                    idsToAccept.includes(solicitud.id)
                        ? { ...solicitud, status: 'accepted' }
                        : solicitud
                )
            );
            setOpenBulkAcceptModal(false);
            setBulkAcceptList([]);
        } catch (err) {
            setError('Error al aceptar las solicitudes en lote: ' + err.message);
        } finally {
            setBulkProcessing(false);
        }
    };

    // Obtener solicitudes del backend
    useEffect(() => {
        const fetchSolicitudes = async () => {
            setLoading(true);
            try {
                const apiKey = process.env.REACT_APP_PANEL_API_KEY || process.env.REACT_APP_INTERNAL_API_KEY || 'ed_qatar_2022_messi_dengueclarasoluna_2026';
                const panelHeaders = {
                    "x-internal-api-key": apiKey,
                    "x-api-key": apiKey
                };

                const [responseMain, responseApelaciones, responseHistorico, responseAsociados, responseCanjes] = await Promise.allSettled([
                    axios.get('https://backend.eldenguee.com/solicitudes/obtener', {
                        headers: panelHeaders
                    }),
                    axios.get('https://backend.eldenguee.com/solicitudes/apelaciones', {
                        headers: panelHeaders
                    }),
                    axios.get('https://backend.eldenguee.com/solicitudes/obtener/historico', {
                        headers: panelHeaders
                    }),
                    axios.get('https://backend.eldenguee.com/solicitudes/bnb/usuarios-asociados', {
                        headers: panelHeaders
                    }),
                    axios.get('https://backend.eldenguee.com/solicitudes/listadoCanjes', {
                        headers: panelHeaders
                    })
                ]);

                let mainData = responseMain.status === 'fulfilled' && Array.isArray(responseMain.value.data) ? responseMain.value.data : [];
                let apelacionesData = responseApelaciones.status === 'fulfilled' && Array.isArray(responseApelaciones.value.data)
                    ? responseApelaciones.value.data.map(ap => ({
                        ...ap,
                        tipo: 'apelacion',
                        casino: 'blacklist',
                        offerScreenshot: ap.imagen || ap.offerScreenshot,
                        motivo: ap.motivo || ap.bnbAddress
                    }))
                    : [];

                setSolicitudes([...mainData, ...apelacionesData]);

                if (responseHistorico.status === 'fulfilled') {
                    const dataHist = responseHistorico.value.data;
                    const rawList = Array.isArray(dataHist) ? dataHist : (Array.isArray(dataHist?.nuevas) ? dataHist.nuevas : []);

                    // Guardar el historial completo para cruzar contra pendientes
                    setSolicitudesHistoricas(rawList);

                    const setNuevas = new Set();
                    rawList.forEach(sol => {
                        const bnb = sol.bnbAddress || sol.bnb_address || sol.bnb;
                        if (bnb && typeof bnb === 'string' && bnb.trim() !== '') {
                            setNuevas.add(bnb.trim().toLowerCase());
                        }
                    });
                    setBnbNuevasSet(setNuevas);
                }

                if (responseAsociados.status === 'fulfilled') {
                    const dataAsociados = Array.isArray(responseAsociados.value.data) ? responseAsociados.value.data : [];
                    const mapAsociados = {};
                    dataAsociados.forEach(item => {
                        const bnb = item.bnbAddress || item.bnb_address || item.bnb;
                        if (bnb && typeof bnb === 'string' && bnb.trim() !== '') {
                            mapAsociados[bnb.trim().toLowerCase()] = item;
                        }
                    });
                    setBnbAsociadosMap(mapAsociados);
                }

                if (responseCanjes.status === 'fulfilled') {
                    const rawCanjes = responseCanjes.value.data;
                    const dataCanjes = Array.isArray(rawCanjes?.canjes)
                        ? rawCanjes.canjes
                        : (Array.isArray(rawCanjes) ? rawCanjes : []);
                    const setQuitar = new Set();
                    const mapCanjes = {};
                    dataCanjes.forEach(item => {
                        const canjeVal = Number(item.canje ?? item.tipoCanje ?? item.idCanje);
                        const userNorm = (item.nombre || item.usuario || item.username || item.nombreUsuario || '').trim().toLowerCase();
                        if (canjeVal === 4 && userNorm) {
                            setQuitar.add(userNorm);
                            mapCanjes[userNorm] = item;
                        }
                    });
                    setCanjesBlacklistSet(setQuitar);
                    setCanjesMap(mapCanjes);
                }
            } catch (err) {
                setError('Error al cargar las solicitudes: ' + err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchSolicitudes();
    }, []);

    const [openRazon, setOpenRazon] = useState(false);
    const [razonRechazo, setRazonRechazo] = useState('');
    const [solicitudRechazadaId, setSolicitudRechazadaId] = useState(null);
    const [agregarBlacklist, setAgregarBlacklist] = useState(false);

    const handleAbrirModalRechazo = (sol) => {
        setSolicitudRechazadaId(sol);
        setRazonRechazo('');
        setAgregarBlacklist(false);
        setOpenRazon(true);
    };


    // Manejar acción de aceptar/rechazar
    const handleAction = async (id, action, razon, nombre, tipo) => {
        setLoading(true);
        setError('');
        console.log(id, action, razon, nombre, tipo)
        const isApelacion = tipo === 'apelacion' || tabValue === 3;
        try {
            let response;
            const apiKey = process.env.REACT_APP_PANEL_API_KEY || process.env.REACT_APP_INTERNAL_API_KEY || 'ed_qatar_2022_messi_dengueclarasoluna_2026';
            const actionHeaders = {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                "x-internal-api-key": apiKey,
                "x-api-key": apiKey
            };
            try {
                response = await axios.put(
                    isApelacion
                        ? `https://backend.eldenguee.com/solicitudes/apelaciones/${id}/update`
                        : `https://backend.eldenguee.com/solicitudes/${id}/update`,
                    { status: action, razon: razon, nombre: nombre, tipo: tipo },
                    { headers: actionHeaders }
                );
            } catch (errPut) {
                // Fallback al endpoint general
                response = await axios.put(
                    `https://backend.eldenguee.com/solicitudes/${id}/update`,
                    { status: action, razon: razon, nombre: nombre, tipo: tipo },
                    { headers: actionHeaders }
                );
            }

            setSolicitudes((prev) =>
                prev.map((solicitud) =>
                    solicitud.id === id ? { ...solicitud, status: response.data?.status || action } : solicitud
                )
            );
        } catch (err) {
            setError(`Error al ${action === 'accepted' ? 'aceptar' : 'rechazar'} la solicitud: ` + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Abrir diálogo para ver captura
    const handleOpenScreenshot = (url, solicitudId) => {
        console.log(url);
        setSelectedScreenshot(url);
        setSelectedSolicitudId(solicitudId);
        setOpenDialog(true);
    };


    const [bnb, setBnb] = useState()
    const [openBnb, setOpenBnb] = useState(false)
    const handleOpenAdrres = (add) => {

        setBnb(add);
        setOpenBnb(true);
    };
    const handleCloseAdrres = (add) => {

        setBnb(null);
        setOpenBnb(false);
    };

    // Cerrar diálogo
    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedScreenshot(null);
        setSelectedSolicitudId(null);
    };
    const [mousePos, setMousePos] = useState({ x: -100, y: -100 });
    const svgRef = useRef(null);

    const handleMouseMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width * 100; // Convert to percentage
        const y = (e.clientY - rect.top) / rect.height * 100;
        setMousePos({ x, y });

        if (svgRef.current) {
            const ellipse = svgRef.current.querySelector('ellipse');
            if (ellipse) {
                ellipse.setAttribute('cx', `${x}%`);
                ellipse.setAttribute('cy', `${y}%`);
            }
        }
    };

    const handleMouseLeave = () => {
        setMousePos({ x: -100, y: -100 });
        if (svgRef.current) {
            const ellipse = svgRef.current.querySelector('ellipse');
            if (ellipse) {
                ellipse.setAttribute('cx', '1.25%'); // Reset to initial position
                ellipse.setAttribute('cy', '82.24121557454892%');
            }
        }
    };
    const [mensajeOCR, setMensajeOCR] = useState("");

    const analizarFechasOCR = (texto, fechaSolicitudStr) => {
        if (!texto) return { tieneFechaMenor: false, fechaEncontrada: null, coincideConSolicitud: false, difiereDeSolicitud: false };

        // Fecha de corte: 6 de junio de 2026
        const FECHA_CORTE = new Date(2026, 5, 6);

        const meses = {
            ENE: 0, ENERO: 0, JAN: 0, JANUARY: 0,
            FEB: 1, FEBRERO: 1, FEBRUARY: 1,
            MAR: 2, MARZO: 2, MARCH: 2,
            ABR: 3, ABRIL: 3, APR: 3, APRIL: 3,
            MAY: 4, MAYO: 4,
            JUN: 5, JUNIO: 5, JUNE: 5,
            JUL: 6, JULIO: 6, JULY: 6,
            AGO: 7, AGOSTO: 7, AUG: 7, AUGUST: 7,
            SEP: 8, SET: 8, SEPTIEMBRE: 8, SETIEMBRE: 8, SEPT: 8, SEPTEMBER: 8,
            OCT: 9, OCTUBRE: 9, OCTOBER: 9,
            NOV: 10, NOVIEMBRE: 10, NOVEMBER: 10,
            DIC: 11, DICIEMBRE: 11, DEC: 11, DECEMBER: 11
        };

        let fechaSolicitudFormatted = null;
        if (fechaSolicitudStr) {
            if (typeof fechaSolicitudStr === 'string' && fechaSolicitudStr.includes('-')) {
                const cleanStr = fechaSolicitudStr.includes('T') ? fechaSolicitudStr.split('T')[0] : fechaSolicitudStr.split(' ')[0];
                const parts = cleanStr.split('-');
                if (parts.length === 3) {
                    const [anio, mes, dia] = parts;
                    fechaSolicitudFormatted = `${String(dia).padStart(2, '0')}/${String(mes).padStart(2, '0')}/${anio}`;
                }
            }
            if (!fechaSolicitudFormatted) {
                const pDate = new Date(fechaSolicitudStr);
                if (!isNaN(pDate.getTime())) {
                    const d = String(pDate.getUTCDate()).padStart(2, '0');
                    const m = String(pDate.getUTCMonth() + 1).padStart(2, '0');
                    const y = pDate.getUTCFullYear();
                    fechaSolicitudFormatted = `${d}/${m}/${y}`;
                }
            }
        }

        const fechasEncontradas = [];

        // 1. Formatos numéricos: DD/MM/YYYY, DD-MM-YYYY, YYYY-MM-DD, etc.
        const regexNumerico = /\b(\d{1,4})[\/\.-](\d{1,2})[\/\.-](\d{1,4})\b/g;
        let match;

        while ((match = regexNumerico.exec(texto)) !== null) {
            let p1 = parseInt(match[1], 10);
            let p2 = parseInt(match[2], 10);
            let p3 = parseInt(match[3], 10);

            let dia, mes, anio;

            if (p1 > 1000) {
                anio = p1;
                mes = p2 - 1;
                dia = p3;
            } else {
                dia = p1;
                mes = p2 - 1;
                anio = p3 < 100 ? (p3 > 50 ? 1900 + p3 : 2000 + p3) : p3;
            }

            if (mes >= 0 && mes <= 11 && dia >= 1 && dia <= 31 && anio >= 2000 && anio <= 2100) {
                const fechaObj = new Date(anio, mes, dia);
                if (!isNaN(fechaObj.getTime())) {
                    const formatted = `${String(dia).padStart(2, '0')}/${String(mes + 1).padStart(2, '0')}/${anio}`;
                    fechasEncontradas.push({ fechaObj, formatted });
                }
            }
        }

        // 2. Meses en texto (ej. "5 DE JUNIO DE 2026", "12 MAY 2026")
        const regexTexto = /\b(\d{1,2})\s+(?:DE\s+)?([A-Z]{3,10})\s+(?:DE\s+)?(\d{2,4})\b/g;
        while ((match = regexTexto.exec(texto)) !== null) {
            const dia = parseInt(match[1], 10);
            const strMes = match[2];
            let pAnio = parseInt(match[3], 10);
            const anio = pAnio < 100 ? (pAnio > 50 ? 1900 + pAnio : 2000 + pAnio) : pAnio;

            const mesIndex = meses[strMes];
            if (mesIndex !== undefined && dia >= 1 && dia <= 31 && anio >= 2000 && anio <= 2100) {
                const fechaObj = new Date(anio, mesIndex, dia);
                if (!isNaN(fechaObj.getTime())) {
                    const formatted = `${String(dia).padStart(2, '0')}/${String(mesIndex + 1).padStart(2, '0')}/${anio}`;
                    fechasEncontradas.push({ fechaObj, formatted });
                }
            }
        }

        let tieneFechaMenor = false;
        let fechaMenorStr = null;
        let coincideConSolicitud = false;
        let primeraFechaEncontrada = fechasEncontradas.length > 0 ? fechasEncontradas[0].formatted : null;

        for (const item of fechasEncontradas) {
            if (item.fechaObj < FECHA_CORTE) {
                tieneFechaMenor = true;
                fechaMenorStr = item.formatted;
            }

            if (fechaSolicitudFormatted && item.formatted === fechaSolicitudFormatted) {
                coincideConSolicitud = true;
            }
        }

        const difiereDeSolicitud = !coincideConSolicitud && fechasEncontradas.length > 0 && !!fechaSolicitudFormatted;

        return {
            tieneFechaMenor: tieneFechaMenor,
            fechaEncontrada: primeraFechaEncontrada || fechaMenorStr,
            fechaSolicitudFormatted: fechaSolicitudFormatted,
            coincideConSolicitud: coincideConSolicitud,
            difiereDeSolicitud: difiereDeSolicitud
        };
    };

    const procesarOCR = async (imageUrl, fechaSolicitudStr) => {
        setMensajeOCR("Procesando imagen...");

        try {
            const result = await Tesseract.recognize(imageUrl, "eng");
            const textoDetectado = result.data.text.toUpperCase();

            const tieneCodigo = textoDetectado.includes("ELDENGUEE");
            const resFecha = analizarFechasOCR(textoDetectado, fechaSolicitudStr);

            if (tieneCodigo) {
                setMensajeOCR("✅ ¡Código ELDENGUEE detectado!");
            } else {
                setMensajeOCR("❌ No se detectó el código ELDENGUEE.");
            }

            return {
                status: tieneCodigo ? 'DETECTADO' : 'ERROR',
                fechaMenor: resFecha.tieneFechaMenor,
                fechaEncontrada: resFecha.fechaEncontrada,
                fechaSolicitudFormatted: resFecha.fechaSolicitudFormatted,
                coincideConSolicitud: resFecha.coincideConSolicitud,
                difiereDeSolicitud: resFecha.difiereDeSolicitud,
                texto: textoDetectado
            };
        } catch (err) {
            console.error("Error OCR:", err);
            setMensajeOCR("❌ Error al procesar la imagen.");
            return {
                status: 'ERROR',
                fechaMenor: false,
                fechaEncontrada: null,
                fechaSolicitudFormatted: null,
                coincideConSolicitud: false,
                difiereDeSolicitud: false,
                texto: ''
            };
        }
    };
    const decodeQRFromUrl = async (imageUrl) => {

        try {
            const reader = new BrowserQRCodeReader();
            const img = new Image();
            img.crossOrigin = 'anonymous'; // Para evitar problemas de CORS
            img.src = imageUrl;

            await new Promise((resolve, reject) => {
                img.onload = () => {
                    if (img.naturalWidth === 0 || img.naturalHeight === 0) {
                        reject(new Error('Imagen cargada pero sin dimensiones válidas'));
                    } else {
                        resolve();
                    }
                };
                img.onerror = () => reject(new Error('Error al cargar la imagen'));
            });

            const result = await reader.decodeFromImageElement(img);

            return result?.getText() || null;
        } catch (err) {
            console.warn('QR no leído:', err);
            return null;
        }
    };

    const analizarIAImagen = async (imageUrl, expectedXbetId = '') => {
        if (!imageUrl) return { isAI: false, reason: null, xbetCheck: null };

        let isAIDetected = false;
        let aiReason = null;

        // 1. Análisis binario de metadatos (EXIF, PNG chunks, XMP, C2PA, SD parameters)
        try {
            const response = await fetch(imageUrl, { mode: 'cors' });
            if (response.ok) {
                const buffer = await response.arrayBuffer();
                const decoderIso = new TextDecoder('iso-8859-1');
                const decoderUtf = new TextDecoder('utf-8', { fatal: false });
                const binaryStr = decoderIso.decode(buffer).toLowerCase() + ' ' + decoderUtf.decode(buffer).toLowerCase();

                const aiKeywords = [
                    { key: 'dall-e', label: 'DALL-E / OpenAI' },
                    { key: 'dalle', label: 'DALL-E / OpenAI' },
                    { key: 'dall·e', label: 'DALL-E / OpenAI' },
                    { key: 'chatgpt', label: 'ChatGPT / OpenAI' },
                    { key: 'openai', label: 'OpenAI' },
                    { key: 'midjourney', label: 'Midjourney' },
                    { key: 'stable diffusion', label: 'Stable Diffusion' },
                    { key: 'stablediffusion', label: 'Stable Diffusion' },
                    { key: 'novelai', label: 'NovelAI' },
                    { key: 'bing image creator', label: 'Bing / Copilot AI' },
                    { key: 'bing.com/create', label: 'Bing AI' },
                    { key: 'copilot', label: 'Microsoft Copilot AI' },
                    { key: 'gemini', label: 'Google Gemini AI' },
                    { key: 'google ai', label: 'Google AI' },
                    { key: 'c2pa', label: 'Metadatos C2PA (Content Credentials IA)' },
                    { key: 'jumbf', label: 'Metadatos C2PA / JUMBF (IA Generativa)' },
                    { key: 'adobe firefly', label: 'Adobe Firefly AI' },
                    { key: 'firefly', label: 'Adobe Firefly AI' },
                    { key: 'aigc', label: 'Generado por IA (AIGC)' },
                    { key: 'generativeai', label: 'IA Generativa' },
                    { key: 'flux', label: 'Flux AI' },
                    { key: 'ideogram', label: 'Ideogram AI' },
                    { key: 'leonardo.ai', label: 'Leonardo AI' },
                    { key: 'recraft', label: 'Recraft AI' },
                    { key: 'seaart', label: 'SeaArt AI' },
                    { key: 'clipdrop', label: 'Clipdrop AI' },
                    { key: 'grok', label: 'Grok / X.AI' },
                    { key: 'meta ai', label: 'Meta AI / Imagine' },
                    { key: 'imagine with', label: 'Meta Imagine AI' },
                    { key: 'automatic1111', label: 'Automatic1111 WebUI AI' },
                    { key: 'comfyui', label: 'ComfyUI AI' },
                    { key: 'fooocus', label: 'Fooocus AI' },
                ];

                for (const { key, label } of aiKeywords) {
                    if (binaryStr.includes(key)) {
                        isAIDetected = true;
                        aiReason = `Metadatos / Firma de IA detectada: ${label}`;
                        break;
                    }
                }

                // Detección de parámetros de generación Stable Diffusion / ComfyUI
                if (
                    !isAIDetected && (
                        (binaryStr.includes('steps:') && binaryStr.includes('sampler:')) ||
                        (binaryStr.includes('cfg scale:') && binaryStr.includes('seed:')) ||
                        (binaryStr.includes('negative prompt:') && binaryStr.includes('model:')) ||
                        binaryStr.includes('generation_parameters')
                    )
                ) {
                    isAIDetected = true;
                    aiReason = 'Parámetros de generación Stable Diffusion / ComfyUI detectados';
                }
            }
        } catch (err) {
            console.warn('Error al analizar binario de imagen para IA:', err);
        }

        // 2. Clasificación por Visión de IA (API de Inferencia HuggingFace)
        try {
            const hfApiUrl = 'https://api-inference.huggingface.co/models/umm-maybe/AI-image-detector';
            const imgBlobResponse = await fetch(imageUrl, { mode: 'cors' });
            if (imgBlobResponse.ok) {
                const imageBlob = await imgBlobResponse.blob();
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 4000);

                const hfRes = await fetch(hfApiUrl, {
                    method: 'POST',
                    body: imageBlob,
                    signal: controller.signal,
                }).catch(() => null);

                clearTimeout(timeoutId);

                if (hfRes && hfRes.ok) {
                    const predictions = await hfRes.json();
                    if (Array.isArray(predictions)) {
                        const aiPrediction = predictions.find(
                            (p) => p.label && (p.label.toLowerCase().includes('artificial') || p.label.toLowerCase().includes('ai') || p.label.toLowerCase().includes('fake'))
                        );
                        if (aiPrediction && aiPrediction.score >= 0.50 && !isAIDetected) {
                            const porcentaje = Math.round(aiPrediction.score * 100);
                            isAIDetected = true;
                            aiReason = `Detector de visión de IA (Probabilidad: ${porcentaje}%)`;
                        }
                    }
                }
            }
        } catch (err) {
            console.warn('Error o timeout al consultar API de clasificación de IA:', err);
        }

        // 3. OCR para texto / firmas de IA Y verificación de ID 1XBET
        let xbetCheck = null;
        const cleanExpected = String(expectedXbetId || '').trim().replace(/[^0-9]/g, '');

        try {
            const result = await Tesseract.recognize(imageUrl, 'eng');
            const rawText = (result?.data?.text || '').toUpperCase();

            // Filtrar leyendas y botones de la interfaz del navegador Chrome/Edge para evitar falsos positivos
            const cleanText = rawText
                .replace(/PREGUNTA[LE]* A GEMINI/g, '')
                .replace(/PREG[ÚU]NTA[LE]* A GEMINI/g, '')
                .replace(/ASK GEMINI/g, '')
                .replace(/PREGUNTA[LE]* A COPILOT/g, '')
                .replace(/PREG[ÚU]NTA[LE]* A COPILOT/g, '')
                .replace(/ASK COPILOT/g, '')
                .replace(/GEMINI/g, '');

            const ocrAIKeywords = [
                { key: 'CHATGPT', label: 'Texto "ChatGPT" detectado en la imagen' },
                { key: 'OPENAI', label: 'Texto "OpenAI" detectado en la imagen' },
                { key: 'DALL-E', label: 'Texto "DALL-E" detectado en la imagen' },
                { key: 'DALLE', label: 'Texto "DALL-E" detectado en la imagen' },
                { key: 'MIDJOURNEY', label: 'Texto "Midjourney" detectado en la imagen' },
                { key: 'STABLE DIFFUSION', label: 'Texto "Stable Diffusion" detectado en la imagen' },
                { key: 'BING IMAGE CREATOR', label: 'Texto "Bing Image Creator" detectado en la imagen' },
                { key: 'GENERATED WITH AI', label: 'Marca de agua IA ("Generated with AI")' },
                { key: 'GENERADO CON IA', label: 'Marca de agua IA ("Generado con IA")' },
                { key: 'MADE WITH AI', label: 'Marca de agua IA ("Made with AI")' },
                { key: 'CREADO CON IA', label: 'Marca de agua IA ("Creado con IA")' },
                { key: 'IMAGINE WITH', label: 'Firma de IA Meta/Imagine' },
                { key: 'IDEOGRAM', label: 'Texto "Ideogram" detectado en la imagen' },
                { key: 'LEONARDO.AI', label: 'Firma "Leonardo.ai" detectada' },
                { key: 'FLUX.1', label: 'Firma "Flux AI" detectada' },
            ];

            if (!isAIDetected) {
                for (const { key, label } of ocrAIKeywords) {
                    if (cleanText.includes(key)) {
                        isAIDetected = true;
                        aiReason = label;
                        break;
                    }
                }
            }

            // ─── 4. Verificación de ID 1XBET ─────────────────────────────────
            if (!cleanExpected) {
                xbetCheck = {
                    status: 'SIN_ID',
                    sinId: true,
                    coincide: false,
                    difiere: false,
                    noDetectado: false,
                    expectedId: null,
                    detectedId: null,
                    message: 'El usuario no tiene ID de 1XBET registrado'
                };
            } else {
                const normText = rawText.replace(/[\s\-_.:#/\\,]/g, '');
                const exactCoincide = rawText.includes(cleanExpected) || normText.includes(cleanExpected);

                // Buscar secuencias de cuenta etiquetadas en la captura (ej: "ID: 12345678", "CUENTA: 12345678", "USER: 12345678")
                const idRegexConEtiqueta = /(?:ID|CUENTA|ACCOUNT|USER|USUARIO|N[°ºO.]+|NO\.)\s*[:#.\-_]?\s*([0-9]{6,12})/gi;
                const candidatosEtiquetados = [];
                let mMatch;
                while ((mMatch = idRegexConEtiqueta.exec(rawText)) !== null) {
                    if (mMatch[1]) candidatosEtiquetados.push(mMatch[1].trim());
                }

                // Números aislados con longitud de ID de 1xBet (7 a 11 dígitos)
                const secuenciasNumericas = (rawText.match(/\b[0-9]{7,11}\b/g) || []).map(s => s.trim());
                const candidatos = [...new Set([...candidatosEtiquetados, ...secuenciasNumericas])];

                if (exactCoincide) {
                    xbetCheck = {
                        status: 'COINCIDE',
                        coincide: true,
                        difiere: false,
                        noDetectado: false,
                        sinId: false,
                        expectedId: cleanExpected,
                        detectedId: cleanExpected,
                        message: `ID 1XBET Coincide (${cleanExpected})`
                    };
                } else if (candidatos.length > 0) {
                    const detected = candidatosEtiquetados.length > 0 ? candidatosEtiquetados[0] : candidatos[0];
                    xbetCheck = {
                        status: 'DIFIERE',
                        coincide: false,
                        difiere: true,
                        noDetectado: false,
                        sinId: false,
                        expectedId: cleanExpected,
                        detectedId: detected,
                        message: `ID 1XBET Difiere (Imagen: ${detected} vs Usuario: ${cleanExpected})`
                    };
                } else {
                    xbetCheck = {
                        status: 'NO_DETECTADO',
                        coincide: false,
                        difiere: false,
                        noDetectado: true,
                        sinId: false,
                        expectedId: cleanExpected,
                        detectedId: null,
                        message: `ID 1XBET (${cleanExpected}) no detectado en captura`
                    };
                }
            }
        } catch (err) {
            console.warn('Error al realizar OCR para IA y 1XBET ID:', err);
            if (cleanExpected) {
                xbetCheck = {
                    status: 'NO_DETECTADO',
                    coincide: false,
                    difiere: false,
                    noDetectado: true,
                    sinId: false,
                    expectedId: cleanExpected,
                    detectedId: null,
                    message: 'Error al procesar OCR en la imagen'
                };
            }
        }

        return {
            isAI: isAIDetected,
            reason: aiReason,
            xbetCheck
        };
    };

    const [verificaciones, setVerificaciones] = useState({});
    const [verificacionCode, setVerificacionCode] = useState({});
    const [verificacionIP, setVerificacionIP] = useState({});
    const [verificacionFingerprint, setVerificacionFingerprint] = useState({});
    const [infoMulticuenta, setInfoMulticuenta] = useState({});

    useEffect(() => {
        const verificarIASolicitudes = async () => {
            const pendientes = solicitudes.filter((s) => s.status === 'pending');

            for (const solicitud of pendientes) {
                const url = tabValue === 0 || tabValue === 3 ? solicitud.offerScreenshot : solicitud.bnbScreenshot;
                if (!url) continue;

                if (verificacionIA[solicitud.id] && !verificacionIA[solicitud.id].loading && verificacionXbetId[solicitud.id]) {
                    continue;
                }

                setVerificacionIA((prev) => ({
                    ...prev,
                    [solicitud.id]: { loading: true, isAI: false, reason: '' }
                }));

                // Obtener ID de 1XBET del usuario si no viene en el registro
                let userXbetId = solicitud.xbet_id || solicitud.xbetId;
                if (!userXbetId && solicitud.nombre) {
                    try {
                        const xRes = await axios.get(`https://backend.eldenguee.com/solicitudes/xbet-id/${encodeURIComponent(solicitud.nombre)}`);
                        if (xRes.data?.xbet_id) {
                            userXbetId = xRes.data.xbet_id;
                            solicitud.xbet_id = userXbetId;
                        }
                    } catch (_) {}
                }

                const res = await analizarIAImagen(url, userXbetId);
                setVerificacionIA((prev) => ({
                    ...prev,
                    [solicitud.id]: { loading: false, isAI: res.isAI, reason: res.reason, xbetCheck: res.xbetCheck }
                }));
                if (res.xbetCheck) {
                    setVerificacionXbetId((prev) => ({
                        ...prev,
                        [solicitud.id]: res.xbetCheck
                    }));
                }
            }
        };

        if (solicitudes.length > 0) {
            verificarIASolicitudes();
        }
    }, [solicitudes, tabValue]);
    useEffect(() => {
        const verificarQRSolicitudes = async () => {
            const resultados = {};
            const pendientes = solicitudes.filter((s) => s.status === 'pending');

            for (const solicitud of pendientes) {
                if (!solicitud.bnbScreenshot || !solicitud.bnbAddress) {
                    resultados[solicitud.id] = 'pending';
                    continue;
                }

                const qrText = await decodeQRFromUrl(solicitud.bnbScreenshot);


                if (qrText?.trim().toLowerCase() === solicitud.bnbAddress.trim().toLowerCase()) {
                    resultados[solicitud.id] = 'ok';
                } else {
                    resultados[solicitud.id] = 'fail';
                }
            }

            setVerificaciones((prev) => ({ ...prev, ...resultados }));
        };

        const verificarCodigo = async () => {
            const resultadosCode = {};
            const resultadosFecha = {};
            const pendientes = solicitudes.filter((s) => s.status === 'pending');

            for (const solicitud of pendientes) {
                const screenshotUrl = solicitud.offerScreenshot || solicitud.bnbScreenshot;
                if (!screenshotUrl) {
                    resultadosCode[solicitud.id] = 'pending';
                    continue;
                }

                const ocrRes = await procesarOCR(screenshotUrl, solicitud.createdAt);
                console.log('ocrRes', ocrRes);

                const statusStr = typeof ocrRes === 'object' && ocrRes !== null ? ocrRes.status : ocrRes;

                if (statusStr === 'DETECTADO') {
                    resultadosCode[solicitud.id] = 'ok';
                } else {
                    resultadosCode[solicitud.id] = 'fail';
                }

                if (typeof ocrRes === 'object' && ocrRes !== null) {
                    const casinoNorm = (solicitud.casino || '').toLowerCase();
                    const tipoNorm = (solicitud.tipo || '').toLowerCase();
                    const esTipeoExtraOVerificar = casinoNorm === 'bonus' || casinoNorm === 'verificar1xbet' || tipoNorm === 'bonusbet' || tipoNorm === 'verificarbet';

                    if (esTipeoExtraOVerificar) {
                        resultadosFecha[solicitud.id] = {
                            esMenor: ocrRes.fechaMenor,
                            fecha: ocrRes.fechaEncontrada,
                            fechaSolicitud: ocrRes.fechaSolicitudFormatted,
                            coincide: ocrRes.coincideConSolicitud,
                            difiere: ocrRes.difiereDeSolicitud
                        };
                    }
                }
            }
            setVerificacionCode((prev) => ({ ...prev, ...resultadosCode }));
            setVerificacionFecha((prev) => ({ ...prev, ...resultadosFecha }));
        };

        const verificarIPsDuplicadas = () => {
            const ipMap = {};
            const resultados = {};

            const pendientes = solicitudes.filter((s) => s.status === 'pending');

            for (const solicitud of pendientes) {
                const ip = solicitud.ip;

                if (!ip) {
                    resultados[solicitud.id] = 'pending';
                    continue;
                }

                // Limpiar "::ffff:" si está presente
                const cleanIp = ip.replace(/^::ffff:/, '');

                if (ipMap[cleanIp]) {
                    // Si ya existe otra solicitud con la misma IP → marcar ambas como duplicadas
                    resultados[solicitud.id] = 'duplicate';
                    resultados[ipMap[cleanIp]] = 'duplicate';
                } else {
                    ipMap[cleanIp] = solicitud.id;
                }
            }

            setVerificacionIP(resultados);
        };

        const verificarFingerprintDuplicados = () => {
            const fingerprintMap = {};  // fp → id primero encontrado
            const nombreMap = {};       // nombre_normalizado → [{ id, fp, nombre }]
            const resultados = {};      // id → 'duplicate' | 'unique' | 'pending' | 'name_dup' | 'hist_name_dup' | 'hist_bnb_dup'
            const infoMulticuenta = {}; // id → string descriptivo

            const pendientes = solicitudes.filter((s) => s.status === 'pending');

            // ── Datos del historial para cruce ────────────────────────────────
            // nombre → [{ fp, bnbAddress, nombre }] de solicitudes ya aceptadas/rechazadas
            const histNombreMap = {};
            // bnbAddress → [{ nombre }] del historial
            const histBnbMap = {};

            for (const h of solicitudesHistoricas) {
                const nombre = (h.nombre || '').trim().toLowerCase();
                const bnb = (h.bnbAddress || h.bnb_address || h.bnb || '').trim().toLowerCase();
                const fp = h.fingerprint || null;

                if (nombre) {
                    if (!histNombreMap[nombre]) histNombreMap[nombre] = [];
                    histNombreMap[nombre].push({ fp, bnb, nombre: h.nombre });
                }
                if (bnb) {
                    if (!histBnbMap[bnb]) histBnbMap[bnb] = [];
                    histBnbMap[bnb].push({ nombre: h.nombre || 'Sin nombre', fp });
                }
            }
            // También incluir las aceptadas que están en solicitudes (mismo endpoint)
            for (const s of solicitudes.filter(s => s.status !== 'pending')) {
                const nombre = (s.nombre || '').trim().toLowerCase();
                const bnb = (s.bnbAddress || '').trim().toLowerCase();
                const fp = s.fingerprint || null;

                if (nombre) {
                    if (!histNombreMap[nombre]) histNombreMap[nombre] = [];
                    histNombreMap[nombre].push({ fp, bnb, nombre: s.nombre });
                }
                if (bnb) {
                    if (!histBnbMap[bnb]) histBnbMap[bnb] = [];
                    histBnbMap[bnb].push({ nombre: s.nombre || 'Sin nombre', fp });
                }
            }

            // Paso 1: agrupar pendientes por nombre
            for (const solicitud of pendientes) {
                const nombre = (solicitud.nombre || '').trim().toLowerCase();
                if (!nombre) continue;
                if (!nombreMap[nombre]) nombreMap[nombre] = [];
                nombreMap[nombre].push({
                    id: solicitud.id,
                    fp: solicitud.fingerprint,
                    nombre: solicitud.nombre || 'Sin nombre',
                    bnb: (solicitud.bnbAddress || '').trim().toLowerCase(),
                });
            }

            // Paso 2: detectar FP exacto duplicado entre pendientes
            for (const solicitud of pendientes) {
                const fp = solicitud.fingerprint;

                if (!fp) {
                    resultados[solicitud.id] = 'pending';
                    continue;
                }

                if (fingerprintMap[fp]) {
                    const idOriginal = fingerprintMap[fp];
                    const nombreActual = solicitud.nombre || 'Sin nombre';
                    const nombreOriginal = pendientes.find(s => s.id === idOriginal)?.nombre || 'Sin nombre';
                    if (nombreActual.trim().toLowerCase() !== nombreOriginal.trim().toLowerCase()) {
                        resultados[solicitud.id] = 'duplicate';
                        resultados[idOriginal] = 'duplicate';
                        infoMulticuenta[solicitud.id] = nombreOriginal;
                        infoMulticuenta[idOriginal] = nombreActual;
                    } else if (!resultados[solicitud.id]) {
                        resultados[solicitud.id] = 'unique';
                    }
                } else {
                    fingerprintMap[fp] = solicitud.id;
                    if (!resultados[solicitud.id]) {
                        resultados[solicitud.id] = 'unique';
                    }
                }
            }

            // Paso 3: mismo nombre entre pendientes con FP distinto (VPN entre pendientes)
            for (const [nombre, grupo] of Object.entries(nombreMap)) {
                if (grupo.length < 2) continue;
                const fps = grupo.map(g => g.fp).filter(Boolean);
                const fpsUnicos = [...new Set(fps)];
                if (fpsUnicos.length < 2) continue;

                for (const entry of grupo) {
                    if (resultados[entry.id] === 'duplicate') continue;

                    const otrosNombres = grupo
                        .filter(g => g.id !== entry.id)
                        .map(g => `${g.nombre} (FP: ${(g.fp || '?').substring(0, 8)}…)`)
                        .join(', ');

                    resultados[entry.id] = 'name_dup';
                    infoMulticuenta[entry.id] = otrosNombres;
                }
            }

            // Paso 4: cruce contra HISTORIAL (aceptadas/rechazadas anteriores)
            for (const solicitud of pendientes) {
                // Saltar si ya tiene una alerta más grave
                if (resultados[solicitud.id] === 'duplicate') continue;

                const nombre = (solicitud.nombre || '').trim().toLowerCase();
                const bnbPending = (solicitud.bnbAddress || '').trim().toLowerCase();
                const fpPending = solicitud.fingerprint;

                // 4a. Mismo nombre en historial con FP distinto → VPN/multicuenta histórica
                const histPorNombre = histNombreMap[nombre] || [];
                const histFpsDist = histPorNombre
                    .filter(h => h.fp && fpPending && h.fp !== fpPending)
                    .map(h => `${h.nombre} (FP: ${h.fp.substring(0, 8)}…)`);

                if (histFpsDist.length > 0) {
                    // Solo sobreescribir 'unique' o 'name_dup'; preservar 'duplicate'
                    if (resultados[solicitud.id] !== 'name_dup') {
                        resultados[solicitud.id] = 'hist_name_dup';
                    }
                    const prev = infoMulticuenta[solicitud.id];
                    infoMulticuenta[solicitud.id] = prev
                        ? prev + ' | Historial: ' + histFpsDist.join(', ')
                        : 'Historial: ' + histFpsDist.join(', ');
                    continue;
                }

                // 4b. Mismo nombre en historial (sin FP registrado antes) → solo nombre repetido
                const histSoloNombre = histPorNombre.filter(h => !h.fp || !fpPending);
                if (histSoloNombre.length > 0 && resultados[solicitud.id] === 'unique') {
                    resultados[solicitud.id] = 'hist_name_dup';
                    infoMulticuenta[solicitud.id] = `${histSoloNombre.length} solicitud${histSoloNombre.length > 1 ? 'es' : ''} aceptada${histSoloNombre.length > 1 ? 's' : ''} con este nombre`;
                    continue;
                }

                // 4c. Mismo BNB address en historial con DISTINTO nombre (ya recibió bono otra persona)
                if (bnbPending && histBnbMap[bnbPending]) {
                    const dupes = histBnbMap[bnbPending];
                    // Solo contar los que tienen un nombre DISTINTO al de la solicitud actual
                    const nombresDistintos = [...new Set(
                        dupes
                            .map(d => d.nombre)
                            .filter(n => n.trim().toLowerCase() !== nombre)
                    )];
                    if (nombresDistintos.length > 0 && (resultados[solicitud.id] === 'unique' || !resultados[solicitud.id])) {
                        resultados[solicitud.id] = 'hist_bnb_dup';
                        infoMulticuenta[solicitud.id] = `BNB ya usada por: ${nombresDistintos.join(', ')}`;
                    }
                }
            }

            setVerificacionFingerprint(resultados);
            setInfoMulticuenta(infoMulticuenta);
        };

        verificarFingerprintDuplicados()
        verificarQRSolicitudes();
        verificarCodigo();
        verificarIPsDuplicadas()
    }, [solicitudes, solicitudesHistoricas]);
    const filterSolicitud = (s) => {
        const casino = s.casino ? s.casino.toLowerCase() : '';
        const tipo = s.tipo ? s.tipo.toLowerCase() : '';


        if (tabValue === 0) {
            // 1xBet
            return casino === '1xbet';

        } else if (tabValue === 3) {
            // Bonus 1xBet
            return casino === 'bonus';
        } else if (tabValue === 1) {
            // Bonus 1xBet
            return casino === 'verificar1xbet';
        } else if (tabValue === 2) {
            // Apelaciones Blacklist
            return tipo === 'apelacion' || casino === 'blacklist';
        } else if (tabValue === 4) {
            // BetWinner
            return tipo === 'normal';
        } else if (tabValue === 5) {
            // Bonus BetWinner
            return tipo == 'bonusbet';
        } else if (tabValue === 6) {
            // Bonus BetWinner
            return tipo == 'verificarbet';
        }

    };

    const handleDescargarBNB = () => {
        if (!solicitudes) return;

        // Filtrar solo los pending
        const pendientes = solicitudes.filter(s => s.status === "pending").filter(filterSolicitud).reverse();

        // Extraer las direcciones BNB
        const direcciones = pendientes
            .map(s => s.bnbAddress)
            .filter(addr => addr && addr.trim() !== "");

        if (direcciones.length === 0) {
            alert("No hay direcciones BNB pendientes para descargar.");
            return;
        }

        // Crear texto con saltos de línea
        const contenido = direcciones.join("\n");

        // Crear archivo TXT
        const blob = new Blob([contenido], { type: "text/plain" });
        const url = URL.createObjectURL(blob);

        // Crear link y descargar
        const a = document.createElement("a");
        a.href = url;
        a.download = "direcciones-bnb.txt";
        a.click();

        URL.revokeObjectURL(url);
    };

    const formatearFecha = (fecha) => {
        const [anio, mes, dia] = fecha.split('T')[0].split('-');

        return `${dia}/${mes}/${anio}`;
    };

    console.log(solicitudes?.filter((sorteo) => sorteo.vpnSuspected === true))
    return (
        <Nlayout>
            <Box
                sx={{
                    mx: 'auto',
                    my: 4,
                    p: 0,
                    minHeight: '100vh',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '50px 0px'
                }}
                className="backgroundAnimado admin-panel"
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
            >
                {/* SVG como fondo */}
                <svg ref={svgRef}
                    xmlns="http://www.w3.org/2000/svg"
                    width="1920"
                    height="1234"
                    fill="none"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: -1 }}
                    className="highlight-block-bg leaderboard-intro-bg">
                    <defs>
                        <radialGradient id="leaderboard_intro_bg_svg__a" cx="0" cy="0" r="1" gradientTransform="rotate(90 452.72 546.22) scale(1141.5)" gradientUnits="userSpaceOnUse">
                            <stop stop-color="#fff"></stop>
                            <stop offset="1" stop-color="#fff"></stop>
                        </radialGradient>
                        <radialGradient id="leaderboard_intro_bg_svg__b" cx="50%" cy="50%" r="50%">
                            <stop offset="0%" stop-color="rgba(100, 100, 100, 1)"></stop>
                            <stop offset="100%" stop-color="rgba(0, 0, 0, 0)"></stop>
                        </radialGradient>
                        <radialGradient id="leaderboard_intro_bg_svg__d" cx="50%" cy="50%" r="50%">
                            <stop offset="0%" stop-color="rgba(20, 20, 20, 0.5)"></stop>
                            <stop offset="100%" stop-color="rgba(20, 20, 20, 0.5)"></stop>
                        </radialGradient>
                        <clipPath id="leaderboard_intro_bg_svg__c">
                            <path fill="url(#leaderboard_intro_bg_svg__a)" fill-opacity="0.16" d="m245.312-362.265-84.854-84.853-28.284 28.284-32.526-32.526 89.095-89.095 32.526 32.526-28.284 28.284 84.853 84.853-32.526 32.527Zm-84.854-93.337 84.854 84.853 24.042-24.042-84.854-84.853 28.284-28.284-24.042-24.042-80.61 80.61 24.042 24.042 28.284-28.284ZM261.712-527.035 155.768-632.979l32.526-32.526 105.944 105.944-32.526 32.526Zm-97.46-105.944 97.46 97.46 24.042-24.042-97.46-97.46-24.042 24.042ZM629.1-660.236l-32.526-32.526 117.378-117.38 32.526 32.526L629.1-660.236Zm-24.042-32.527 24.042 24.042 108.894-108.894-24.042-24.042-108.894 108.894ZM-39.453 508.172l-32.527-32.527L45.4 358.266l32.526 32.527-117.379 117.379Zm-24.043-32.527 24.043 24.043L69.442 390.793 45.4 366.75-63.496 475.645ZM1414.35 1107.77l-32.53-32.53 86.82-86.819-52.32-52.326-56.57 56.568-32.53-32.527 89.1-89.094 84.85 84.852 24.04-24.041-84.85-84.854 32.53-32.527 84.85 84.853 24.04-24.041-84.85-84.854 32.53-32.527 117.37 117.381-28.28 28.283 72.4 72.404L1793.33 873.6l32.52 32.527-134.9 134.893-104.93-104.927-24.04 24.041 84.85 84.856-32.52 32.52-84.85-84.849-115.11 115.109Zm-24.04-32.53 24.04 24.04 115.1-115.102 84.86 84.852 24.04-24.04-84.86-84.855 32.53-32.525 104.93 104.93 126.42-126.412-24.05-24.043-102.37 102.371-80.89-80.889 28.29-28.283-108.9-108.896-24.04 24.043 84.85 84.854-32.52 32.525-84.86-84.854-24.04 24.043 84.86 84.854-32.53 32.525-84.85-84.852-80.61 80.609 24.04 24.043 56.57-56.568 60.81 60.811-86.82 86.819ZM471.859 1001.88 354.48 884.494l28.284-28.283-52.326-52.326-56.569 56.568-32.526-32.527 89.096-89.094 84.853 84.852 24.042-24.041-61.062-61.063 32.526-32.525 61.062 61.061 136.318-136.318 32.526 32.527L532.67 771.357l52.326 52.326 56.569-56.568 32.526 32.527-89.096 89.094-84.852-84.852-24.042 24.041 84.853 84.853-32.526 32.527-84.853-84.853-24.041 24.041 84.852 84.854-32.527 32.533ZM362.965 884.494 471.86 993.39l24.042-24.043-84.854-84.854 32.527-32.525 84.853 84.853 24.042-24.043-84.853-84.853 32.526-32.525 84.852 84.852 80.611-80.609-24.042-24.043-56.569 56.568-60.81-60.811 108.034-108.033-24.042-24.043L471.859 775.6l-61.062-61.061-24.042 24.041 61.062 61.063-32.526 32.525-84.853-84.852-80.611 80.609 24.042 24.043 56.569-56.568 60.811 60.811-28.284 28.283ZM840.17 283.479l-32.527-32.525 150.461-150.46-52.326-52.327-42.427 42.427-32.526-32.526 74.953-74.953 84.853 84.853 24.039-24.042-58.112-58.114 32.526-32.526L1047.2 11.4l24.04-24.042-86.68-86.68 32.53-32.526 119.2 119.206-28.28 28.284 24.04 24.041 198.4-198.401 32.53 32.526-230.93 230.93-56.57-56.568-24.04 24.042 84.85 84.853-32.52 32.526-84.86-84.851-178.74 178.739Zm-24.041-32.526 24.041 24.041 178.74-178.743 84.86 84.853 24.04-24.042-84.85-84.853 32.52-32.528 56.57 56.57 222.45-222.445-24.05-24.042-198.4 198.403-32.52-32.527 28.28-28.284-110.72-110.722-24.046 24.043 86.676 86.68-32.52 32.525-58.116-58.114-24.042 24.042 58.118 58.114-32.529 32.529-84.853-84.856-66.469 66.47 24.042 24.041 42.427-42.428 60.81 60.813-150.459 150.46ZM1142.1-620.959l-32.53-32.526 37.33-37.323-24.05-24.041-56.56 56.567-32.53-32.526 89.1-89.095 56.56 56.568 24.04-24.042-98.99-98.995 32.53-32.527 98.99 98.996 24.04-24.042-56.57-56.569 32.53-32.526 89.1 89.096-28.29 28.284 52.33 52.326 42.42-42.427 32.53 32.526-74.95 74.953-84.86-84.852-24.04 24.041 56.57 56.568-32.53 32.526-56.56-56.568-65.61 65.608Zm-24.04-32.526 24.04 24.042 65.61-65.608 56.56 56.569 24.05-24.042-56.57-56.568 32.52-32.526 84.86 84.853 66.47-66.469-24.05-24.042-42.42 42.427-60.81-60.811 28.28-28.284-80.61-80.611-24.04 24.042 56.57 56.569-32.53 32.527-99-98.996-24.04 24.042 99 98.995-32.53 32.526-56.57-56.568-80.61 80.61 24.05 24.042 56.56-56.568 32.53 32.526-37.32 37.323Z"></path>
                            <path fill="url(#leaderboard_intro_bg_svg__a)" fill-opacity="0.16" d="m67.57 913.486-89.096-89.094 84.852-84.854-24.04-24.041-84.854 84.852-32.527-32.525L6.76 682.971-129.56 546.653l32.528-32.524L11.001 622.161l52.326-52.327L6.76 513.266l32.526-32.526 89.095 89.095-84.852 84.853L67.57 678.73l84.852-84.854 32.527 32.527-84.853 84.854 24.042 24.041 84.854-84.853 32.526 32.528-117.38 117.378-28.284-28.283-52.326 52.327 56.569 56.568-32.528 32.523Zm-80.61-89.093 80.61 80.609 24.04-24.04-56.57-56.569 60.811-60.81 28.284 28.282 108.896-108.894-24.042-24.043-84.854 84.853-32.527-32.525 84.854-84.854-24.043-24.043-84.852 84.854-32.526-32.527 84.853-84.853-80.61-80.61-24.042 24.043 56.568 56.567-60.81 60.812-108.034-108.034-24.042 24.041L15.244 682.97l-84.853 84.853 24.042 24.042 84.853-84.853 32.526 32.525-84.853 84.856ZM635.483 1207.44l-82.531-82.53 131.521-131.52 98.994 98.99-67.882 67.89-32.526-32.53 35.355-35.35-33.941-33.95-66.469 66.47 50.005 50.01-32.526 32.52Zm-74.046-82.53 74.047 74.05 24.042-24.04-50.005-50.01 74.953-74.95 42.426 42.43-35.354 35.35 24.041 24.04 59.397-59.39-90.51-90.51-123.037 123.03ZM1489.16 709.793l-82.72-82.71 32.53-32.527 50.19 50.185 33.94-33.94-81.3-81.3-50.01 50.006-32.52-32.527 82.53-82.531 146.35 146.353-98.99 98.991Zm-74.23-82.71 74.23 74.226 90.51-90.51-137.87-137.867-74.05 74.047 24.05 24.043 50-50.006 89.78 89.783-42.42 42.426-50.19-50.186-24.04 24.044ZM1720.91 860.291l-153.24-153.242 199.92-199.92 169.7 169.705-67.88 67.883-32.52-32.527 35.35-35.355-104.65-104.652-134.87 134.867 120.72 120.715-32.53 32.526-144.76-153.242 144.76 144.758 24.04-24.043-120.71-120.715 143.35-143.352 113.14 113.137-35.36 35.355 24.04 24.043 59.4-59.398-161.22-161.221-191.44 191.436ZM1117.1 405.506l-98.99-98.994 67.88-67.882 32.53 32.526-35.36 35.356 33.94 33.941 66.47-66.469-50-50.005 32.52-32.526 82.54 82.531-131.53 131.522Zm-90.51-98.994 90.51 90.51 123.04-123.037-74.05-74.046-24.04 24.041 50.01 50.005-74.96 74.953-42.42-42.426 35.35-35.356-24.04-24.041-59.4 59.397ZM324.756-589.998 193.234-721.52l98.994-98.996 67.883 67.883-32.527 32.526-35.355-35.354-33.942 33.941 66.469 66.469 50.005-50.005 32.526 32.526-82.531 82.532ZM201.719-721.52l123.037 123.037 74.047-74.047-24.042-24.042-50.005 50.005-74.953-74.953 42.426-42.426 35.355 35.355 24.042-24.042-59.397-59.396-90.51 90.509ZM-22.125 1109.24l-32.526-32.52 56.568-56.57-24.042-24.044-56.568 56.564-32.526-32.52 89.095-89.098 89.096 89.098-89.097 89.09Zm-24.042-32.52 24.042 24.04 80.611-80.61-80.61-80.614-80.611 80.614 24.042 24.04 56.568-56.568 32.528 32.528-56.57 56.57ZM1977.88 1109.24l-32.53-32.52 56.57-56.57-24.04-24.044-56.57 56.564-32.53-32.52 89.1-89.098 89.09 89.098-89.09 89.09Zm-24.05-32.52 24.05 24.04 80.61-80.61-80.61-80.614-80.61 80.614 24.04 24.04 56.57-56.568 32.52 32.528-56.57 56.57ZM1050.1 1149.57l-79.823-79.82 89.093-89.098 79.82 79.818-32.52 32.53-47.3-47.29-24.04 24.04 47.3 47.29-32.53 32.53Zm-71.337-79.82 71.337 71.33 24.04-24.04-47.29-47.29 32.52-32.53 47.3 47.3 24.04-24.05-71.34-71.333-80.607 80.613ZM451.97 699.299l-205.269-205.27-24.043 24.043 92.735 92.734-32.527 32.525-125.261-125.26 89.096-89.096L484.496 666.77l-32.526 32.529ZM246.701 485.545 451.97 690.814l24.042-24.043-229.311-229.31-80.611 80.611 116.776 116.776 24.042-24.041-92.734-92.734 32.527-32.528ZM1325.8 158.404l-32.53-32.526 143.53-143.53-24.04-24.041-62.63 62.631-32.53-32.526 95.16-95.158 89.09 89.095-176.05 176.055Zm-24.05-32.526 24.05 24.042 167.57-167.572-80.61-80.61-86.68 86.674 24.05 24.042 62.63-62.632 32.52 32.526-143.53 143.53ZM1458.5-324.751l-32.53-32.527 46.28-46.279-24.04-24.041-82.16 82.162-32.53-32.526 114.69-114.691 89.1 89.096-78.81 78.806Zm-24.04-32.527 24.04 24.042 70.32-70.321-80.61-80.609L1342-377.962l24.05 24.042 82.16-82.164 32.52 32.527-46.27 46.279ZM1339.43-449.146l-129.02-129.025 32.52-32.526 96.5 96.498 24.04-24.042-56.56-56.568 32.52-32.526 89.1 89.095-89.1 89.094Zm-120.54-129.025 120.54 120.541 80.61-80.611-80.61-80.61-24.04 24.042 56.57 56.568-32.53 32.526-96.5-96.498-24.04 24.042ZM184.685-197.877 80.145-302.418l89.094-89.095 89.096 89.095-32.526 32.526-56.569-56.568-24.042 24.042 72.015 72.015-32.528 32.526ZM88.628-302.418l96.057 96.057 24.042-24.042-72.015-72.015 32.526-32.526 56.569 56.568 24.042-24.042-80.611-80.61-80.61 80.61ZM487.678-639.864l-32.526-32.526 84.853-84.853-24.042-24.042-84.853 84.853-32.526-32.526 117.378-117.38 89.096 89.095-117.38 117.379Zm-24.042-32.527 24.042 24.042 108.895-108.894-80.611-80.61-108.895 108.894 24.042 24.042 84.853-84.853 32.526 32.526-84.852 84.853ZM-7.41 370.57l-89.096-89.094 32.526-32.527 29.372 29.374 77.782-77.785 30.351 30.353-77.78 77.781 29.371 29.373L-7.41 370.57Zm-80.61-89.092 80.61 80.608 24.042-24.041-29.372-29.373 77.781-77.781-21.866-21.866-77.782 77.782-29.372-29.373-24.042 24.044ZM1730.23 1211.53l-56.73-56.74-20.76 20.76-32.53-32.53 80.48-80.48 32.53 32.53-29.38 29.37 56.74 56.73-30.35 30.36Zm-56.74-65.22 56.74 56.73 21.86-21.87-56.73-56.73 29.37-29.37-24.04-24.04-71.99 71.99 24.04 24.04 20.75-20.75ZM410.027 1216.35l-89.095-89.09 32.526-32.53 29.372 29.37 77.781-77.78 30.351 30.36-77.781 77.78 29.371 29.37-32.525 32.52Zm-80.61-89.09 80.61 80.61 24.042-24.04-29.373-29.37 77.783-77.79-21.867-21.86-77.781 77.78-29.372-29.37-24.042 24.04ZM882.201 1142.45l-130.889-130.89 66.638-66.641 32.526 32.528-34.111 34.113 98.362 98.36-32.526 32.53Zm-122.404-130.89 122.404 122.4 24.042-24.04-98.362-98.36 34.111-34.112-24.042-24.042-58.153 58.154ZM952.238-133.342 848.21-237.37l-54.197 54.197-32.526-32.526 86.724-86.724 136.554 136.554-32.527 32.527ZM848.21-245.854l104.028 104.027 24.041-24.042L848.21-293.938l-78.239 78.239 24.042 24.042 54.197-54.197ZM577.283 200.265 397.429 20.41l88.503-88.503 32.526 32.526-55.977 55.977L609.81 167.738l-32.527 32.527ZM405.913 20.41l171.37 171.371 24.042-24.043L453.997 20.41l55.977-55.977-24.042-24.042-80.019 80.02ZM1757.33-496.237l-49.5-49.497-14.14 14.143-32.53-32.527 46.67-46.669 82.03 82.024-32.53 32.526Zm-49.5-57.984 49.5 49.499 24.04-24.042-73.54-73.54-38.18 38.185 24.04 24.041 14.14-14.143ZM405.504-317.482l-67.882-67.882 32.526-32.526 35.356 35.355 28.284-28.284 32.526 32.526-60.81 60.811Zm-59.398-67.882 59.398 59.397 52.326-52.326-24.042-24.042-28.284 28.284-35.356-35.355-24.042 24.042ZM545.786-62.885 513.26-95.41l156.41-156.411-28.283-28.284 32.526-32.528 60.811 60.813L545.786-62.885ZM521.744-95.41l24.042 24.043 180.452-180.454-52.325-52.326-24.042 24.042 28.284 28.284L521.744-95.411ZM496.144-416.531l-55.331-55.331-40.871 40.872-32.526-32.526 73.398-73.398 87.857 87.857-32.527 32.526Zm-55.331-63.816 55.331 55.331 24.042-24.042-79.373-79.373-64.913 64.914 24.042 24.042 40.871-40.872Z"></path>
                            <path fill="url(#leaderboard_intro_bg_svg__a)" fill-opacity="0.16" d="m1124.86 975.455-32.53-32.527 109.57-109.563 49.49 49.496 24.04-24.041-192.33-192.334 216.24-216.236 32.53 32.527-183.72 183.709 159.81 159.807 84.85-84.854-98.99-98.994-24.04 24.041 70.71 70.712-32.53 32.526-103.23-103.238 89.09-89.094 164.05 164.047-206.48 206.474-49.49-49.496-77.04 77.038Zm-24.04-32.527 24.04 24.043 77.03-77.037 49.5 49.497 197.99-197.991-155.56-155.563-80.61 80.609 94.75 94.754 24.04-24.042-70.71-70.712 32.53-32.524 107.48 107.477-93.34 93.338-168.29-168.291 183.71-183.709-24.04-24.043-207.75 207.752 192.33 192.334-32.53 32.525-49.5-49.496-101.07 101.079ZM1617.82 580.121l-89.1-89.095 32.53-32.527 56.57 56.569 205.25-205.254-84.85-84.852-144.45 144.44 24.05 24.043 102.43-102.435 32.53 32.526-134.96 134.961-89.1-89.096 209.5-209.494 206.47 206.475-94.94 94.943 28.28 28.285-32.52 32.525-60.81-60.811 94.94-94.943-24.04-24.041-237.78 237.781Zm-80.61-89.095 80.61 80.61 237.78-237.778 32.52 32.524-94.94 94.943 52.33 52.326 24.04-24.041-28.29-28.285 94.95-94.943-197.99-197.989-201.01 201.009 80.61 80.611 126.47-126.476-24.04-24.042-102.43 102.434-32.53-32.527 152.93-152.924 93.33 93.336-213.73 213.738-56.57-56.569-24.04 24.043ZM800.718 191.221 618.167 8.671l-24.042 24.041 71.615 71.616-32.526 32.526L529.072 32.712l206.475-206.475L871.311-37.998l-89.095 89.095-74.953-74.953 32.526-32.526 42.427 42.426 24.042-24.042-70.711-70.711-84.853 84.854 150.024 150.023 28.284-28.285 32.526 32.528-60.81 60.81ZM618.167.186l182.551 182.551 52.326-52.326-24.042-24.042-28.284 28.284L642.209-23.856l93.338-93.338 79.195 79.195L782.216-5.47l-42.427-42.428-24.042 24.042 66.469 66.47 80.61-80.61-127.279-127.281-197.99 197.99 95.657 95.658 24.042-24.042-71.615-71.616L618.167.186ZM1014.32-199.637 807.842-406.111l87.159-87.159-28.284-28.284 32.526-32.526 60.811 60.81-87.159 87.159 24.042 24.042 164.053-164.049 89.09 89.095-32.52 32.527-56.57-56.569-131.526 131.522 84.856 84.854 70.71-70.711-24.04-24.042-49.5 49.497-32.526-32.526 82.026-82.024 89.09 89.096-135.76 135.762ZM816.326-406.111l197.994 197.99L1141.6-335.4l-80.61-80.611-73.542 73.54 24.042 24.042 49.5-49.497 32.52 32.526-79.19 79.195-93.34-93.338 140.01-140.007 56.57 56.568 24.04-24.042-80.61-80.61-164.052 164.049-32.526-32.526 87.159-87.159-52.327-52.326-24.042 24.042 28.284 28.284-87.16 87.159ZM-7.81 194.232-214.285-12.243l49.497-49.497-28.284-28.285 32.526-32.526 60.811 60.812-49.497 49.497L-125.19 11.8 67.144-180.533 169.996-77.68 137.47-45.154 67.144-115.48-92.663 44.326-7.81 129.18l98.995-98.995L67.143 6.143l-70.71 70.71-32.527-32.525L67.144-58.912l89.095 89.096L-7.81 194.232ZM-205.8-12.242l197.99 197.99L147.754 30.185l-80.61-80.61-94.753 94.752L-3.567 68.37 67.144-2.343 99.67 30.185-7.81 137.664l-93.338-93.338L67.144-123.965l70.326 70.327 24.042-24.043-94.368-94.368-192.333 192.333-32.526-32.526 49.497-49.497-52.326-52.327-24.042 24.042 28.284 28.285-49.498 49.497ZM1992.19 194.232 1785.71-12.243l49.5-49.497-28.28-28.285 32.52-32.526 60.82 60.812-49.5 49.497 24.04 24.041 192.33-192.332L2170-77.68l-32.53 32.526-70.32-70.326-159.81 159.808 84.85 84.852 99-98.995-24.04-24.042-70.72 70.71-32.52-32.525L2067.15-58.91l89.09 89.095-164.05 164.046ZM1794.2-12.242l197.99 197.99 155.56-155.563-80.61-80.61-94.75 94.752 24.04 24.042 70.71-70.712 32.53 32.528-107.48 107.479-93.34-93.338 168.29-168.291 70.33 70.325 24.04-24.04-94.37-94.369-192.33 192.333-32.52-32.526 49.49-49.497-52.32-52.327-24.05 24.042 28.29 28.285-49.5 49.497ZM314.482-408.618l-60.811-60.812 182.025-182.025 32.526 32.526-149.497 149.498 28.284 28.284-32.527 32.529Zm0-8.486 24.042-24.043-28.284-28.284L459.739-618.93l-24.042-24.042-173.541 173.541 52.326 52.327ZM1578.53-637.021 1478.55-737l99.98-99.979 32.53 32.526L1543.61-737l67.45 67.452-32.53 32.527ZM1487.04-737l91.49 91.494 24.04-24.042L1535.12-737l67.45-67.452-24.04-24.042L1487.04-737ZM545.641-176.463l-114.12-114.12 32.526-32.528 81.594 81.596 39.168-39.168 32.526 32.526-71.694 71.694Zm-105.636-114.12 105.636 105.636 63.21-63.21-24.042-24.042-39.168 39.168-81.594-81.595-24.042 24.043ZM1578.53 1362.98l-99.98-99.98 99.98-99.98 32.53 32.53-67.45 67.45 67.45 67.45-32.53 32.53Zm-91.49-99.98 91.49 91.49 24.04-24.04-67.45-67.45 67.45-67.45-24.04-24.04-91.49 91.49ZM1420.13 1228.85l-191.2-191.2 32.52-32.53 158.68 158.67 71.89-71.89 32.53 32.52-104.42 104.43Zm-182.72-191.2 182.72 182.71 95.94-95.94-24.05-24.04-71.89 71.9-158.68-158.68-24.04 24.05ZM225.003 1175.61l-121.839-121.83 32.526-32.53 89.313 89.31 152.661-152.659 32.526 32.525-185.187 185.184Zm-113.355-121.83 113.355 113.35 176.703-176.704-24.042-24.041-152.661 152.665-89.313-89.32-24.042 24.05ZM1008.18 730.773l-84.382-84.383 99.982-99.978 32.52 32.527-67.448 67.451 51.858 51.855-32.53 32.528Zm-75.898-84.382 75.898 75.898 24.04-24.043-51.854-51.855 67.454-67.451-24.05-24.043-91.488 91.494ZM558.869 433.33l-84.832-84.834 32.526-32.525 52.306 52.305 67.452-67.451 32.526 32.528-99.978 99.977Zm-76.348-84.834 76.348 76.35 91.494-91.493-24.042-24.044-67.452 67.451-52.306-52.305-24.042 24.041Z"></path>
                            <path fill="url(#leaderboard_intro_bg_svg__a)" fill-opacity="0.16" d="m1175.74-361.151-32.53-32.526 67.45-67.451-87.68-87.68 32.53-32.526 120.2 120.206-99.97 99.977Zm-24.05-32.527 24.05 24.042 91.49-91.493-111.72-111.722-24.04 24.042 87.68 87.68-67.46 67.451ZM187.984-96.727l-99.979-99.978 32.526-32.526 67.452 67.451 52.004-52.004 32.526 32.526-84.529 84.531ZM96.49-196.705l91.494 91.494 76.046-76.047-24.042-24.042-52.004 52.004-67.452-67.451-24.042 24.042ZM131.725-657.021 31.747-757l99.978-99.979 32.527 32.526L96.8-757l67.452 67.452-32.527 32.527ZM40.231-757l91.493 91.494 24.042-24.042L88.316-757l67.45-67.452-24.042-24.042L40.231-757ZM16.726-288.405l-99.979-99.979 99.98-99.979 32.525 32.526-67.452 67.453 67.452 67.452-32.526 32.527Zm-91.495-99.979 91.494 91.494 24.042-24.042-67.452-67.452 67.452-67.452-24.042-24.042-91.494 91.494ZM-22-678.549l-32.526-32.526L-22-743.602l32.526 32.526L-22-678.549Zm-24.042-32.526L-22-687.033l24.042-24.042L-22-735.117l-24.042 24.042ZM1978-678.549l-32.53-32.526 32.53-32.527 32.53 32.526-32.53 32.527Zm-24.04-32.526 24.04 24.042 24.04-24.042-24.04-24.042-24.04 24.042ZM346.363 707.826l-32.526-32.527 32.526-32.525 32.526 32.525-32.526 32.527Zm-24.042-32.527 24.042 24.043 24.042-24.043-24.042-24.041-24.042 24.041ZM792.2 1196.53 759.674 1164l32.526-32.52 32.527 32.52-32.527 32.53ZM768.158 1164l24.042 24.04 24.042-24.04-24.042-24.04-24.042 24.04ZM910.485 946.783l-32.526-32.524 32.526-32.528 32.526 32.528-32.526 32.524Zm-24.042-32.524 24.042 24.04 24.042-24.04-24.042-24.044-24.042 24.044ZM1699.08 936.348l-32.52-32.527 32.52-32.525 32.53 32.525-32.53 32.527Zm-24.04-32.528 24.04 24.043 24.04-24.043-24.04-24.041-24.04 24.041ZM75.067 1128.53l-32.526-32.52 32.526-32.53 32.526 32.53-32.526 32.52Zm-24.042-32.52 24.042 24.04 24.042-24.04-24.042-24.05-24.042 24.05ZM371.707 495.813l-93.989-93.99 32.526-32.525 93.989 93.988-32.526 32.527Zm-85.505-93.991 85.505 85.506 24.042-24.043-85.505-85.504-24.042 24.041ZM378.625 372.146l-32.526-32.527 69.032-69.034 32.526 32.528-69.032 69.033Zm-24.042-32.529 24.042 24.044 60.548-60.55-24.042-24.043-60.548 60.549ZM1179.88 1039.21l-32.52-32.52 57.52-57.532 32.53 32.525-57.53 57.527Zm-24.04-32.52 24.04 24.04 49.05-49.046-24.05-24.041-49.04 49.047ZM89.156 1004.26 56.63 971.733l69.032-69.031 32.528 32.525-69.034 69.033Zm-24.042-32.528 24.042 24.043 60.548-60.549-24.042-24.041-60.548 60.547ZM223.843 803.092l-32.526-32.527 69.032-69.031 32.526 32.525-69.032 69.033Zm-24.042-32.528 24.042 24.043 60.548-60.549-24.042-24.041-60.548 60.547ZM103.89 364.828l-32.526-32.526 63.134-63.134 32.526 32.527-63.134 63.133Zm-24.042-32.526 24.042 24.042 54.649-54.647-24.042-24.044-54.649 54.649ZM-22.007 1235.24l-32.526-32.52 71.098-71.1 32.527 32.53-71.099 71.09Zm-24.042-32.52 24.042 24.04 62.613-62.61-24.042-24.05-62.613 62.62ZM1977.99 1235.24l-32.52-32.52 71.1-71.1 32.52 32.53-71.1 71.09Zm-24.04-32.52 24.04 24.04 62.62-62.61-24.05-24.05-62.61 62.62ZM946.802 1217.48l-32.526-32.52 63.134-63.14 32.53 32.53-63.138 63.13Zm-24.042-32.52 24.042 24.04 54.648-54.65-24.041-24.04-54.649 54.65ZM926.426-38.184 825.988-138.622l32.526-32.526L958.952-70.71l-32.526 32.526Zm-91.953-100.438 91.953 91.954 24.042-24.042-91.953-91.954-24.042 24.042ZM1663.97-289.264l-32.53-32.526 32.53-32.526 32.53 32.526-32.53 32.526Zm-24.04-32.526 24.04 24.042 24.04-24.042-24.04-24.042-24.04 24.042ZM1610.93-3.812l-32.53-32.526 32.53-32.526 32.53 32.526-32.53 32.526Zm-24.04-32.526 24.04 24.042 24.04-24.042-24.04-24.042-24.04 24.042ZM1682.21 162.612l-32.53-32.526 32.53-32.526 32.52 32.526-32.52 32.526Zm-24.04-32.526 24.04 24.042 24.04-24.042-24.04-24.042-24.04 24.042ZM1501.99 465.078l-32.52-32.527 32.52-32.525 32.53 32.525-32.53 32.527Zm-24.04-32.527 24.04 24.043 24.05-24.043-24.05-24.041-24.04 24.041ZM1738.54 723.135l-32.52-32.525 65.39-65.397 32.53 32.525-65.4 65.397Zm-24.04-32.526 24.04 24.041 56.91-56.912-24.04-24.041-56.91 56.912ZM1874.36-332.867l-32.53-32.526 32.53-32.526 32.53 32.526-32.53 32.526Zm-24.04-32.527 24.04 24.042 24.04-24.042-24.04-24.042-24.04 24.042ZM1723.47-348.762l-32.53-32.526 60.81-60.811 32.53 32.526-60.81 60.811Zm-24.04-32.526 24.04 24.042 52.33-52.326-24.05-24.042-52.32 52.326ZM397.102-39.396l-32.526-32.526 64.711-64.713 32.526 32.528-64.711 64.711ZM373.06-71.922l24.042 24.042 56.227-56.227-24.042-24.042-56.227 56.227ZM1086.99-560.157l-32.52-32.528 32.52-32.526 32.53 32.526-32.53 32.528Zm-24.04-32.529 24.04 24.042 24.04-24.042-24.04-24.042-24.04 24.042ZM1304.62-384.8l-32.53-32.526 32.53-32.526 32.52 32.526-32.52 32.526Zm-24.05-32.526 24.05 24.042 24.04-24.042-24.04-24.042-24.05 24.042ZM544.175 307.463l-32.526-32.525 32.526-32.528 32.526 32.528-32.526 32.525Zm-24.042-32.525 24.042 24.041 24.042-24.041-24.042-24.042-24.042 24.042ZM1027.08 231.323l-32.529-32.526 32.529-32.526 32.52 32.526-32.52 32.526Zm-24.04-32.526 24.04 24.042 24.04-24.042-24.04-24.042-24.04 24.042ZM773.436-440.41l-32.526-32.526 52.53-52.531 32.526 32.526-52.53 52.531Zm-24.042-32.527 24.042 24.042 44.046-44.047-24.042-24.042-44.046 44.047ZM1547.98-439.383l-52.53-52.53 32.53-32.526 52.52 52.53-32.52 32.526Zm-44.05-52.53 44.05 44.046 24.04-24.042-44.04-44.046-24.05 24.042Z"></path>
                            <path fill="url(#leaderboard_intro_bg_svg__a)" fill-opacity="0.16" d="m75.647-362.223-32.526-32.526 32.526-32.526 32.526 32.526-32.526 32.526Zm-24.042-32.526 24.042 24.042 24.042-24.042-24.042-24.042-24.042 24.042ZM71.735-478.199l-197.989-197.99-52.326 52.326 56.568 56.569-32.526 32.526-89.097-89.096 117.381-117.379 197.99 197.99 24.042-24.042-78.095-78.095 32.527-32.525 110.621 110.621-89.096 89.095Zm-197.989-206.475 197.99 197.99 80.61-80.61L50.21-669.431l-24.042 24.042 78.096 78.095-32.528 32.526-197.989-197.99-108.894 108.895 80.61 80.61 24.042-24.041-56.568-56.569 60.809-60.811ZM666.944 625.996l-117.38-117.381 197.99-197.988-80.583-80.583-38.303 38.302-32.526-32.526 70.829-70.829 145.636 145.637-197.99 197.988 52.327 52.328 29.432-29.434 32.526 32.527-61.958 61.959ZM558.049 508.615l108.896 108.897 53.474-53.475-24.042-24.043-29.432 29.434-60.812-60.813 197.99-197.988-137.151-137.151-62.345 62.344 24.042 24.042 38.303-38.302 89.067 89.067-197.99 197.988ZM1472.19-119.007l-201.45-201.446-52.32 52.326 75.41 75.411-32.53 32.526-107.94-107.938 117.38-117.379 201.45 201.445 24.04-24.041-56.57-56.568 32.53-32.526 89.09 89.095-89.09 89.095Zm-201.45-209.93 201.45 201.446 80.61-80.61-80.61-80.61-24.04 24.042 56.56 56.568-32.52 32.527-201.45-201.447-108.89 108.894 99.45 99.453 24.04-24.042-75.41-75.411 60.81-60.81ZM711.071-502.841l-89.095-89.095 117.379-117.38 96.444 96.444 24.042-24.042-35.434-35.434 32.526-32.526 67.96 67.96-89.093 89.096-96.444-96.444-52.326 52.327 56.568 56.568-32.527 32.526Zm-80.61-89.095 80.61 80.61 24.042-24.042-56.568-56.568 60.81-60.812 96.444 96.445 80.61-80.61-59.476-59.476-24.042 24.042 35.434 35.434-32.525 32.526-96.444-96.444-108.895 108.895ZM225.258 1047.86l-60.811-60.814 32.526-32.527 28.284 28.285 24.042-24.043-84.853-84.852 32.526-32.527 117.379 117.379-89.093 89.099Zm-52.326-60.813 52.326 52.323 80.61-80.608-108.895-108.895-24.042 24.043 84.853 84.852-32.526 32.527-28.284-28.285-24.042 24.043ZM981.943 876.148l-89.096-89.096 60.811-60.811 32.527 32.525-28.284 28.286 24.042 24.041 84.857-84.853 32.52 32.526-117.377 117.382Zm-80.611-89.095 80.611 80.611 108.897-108.896-24.05-24.04-84.848 84.851-32.528-32.525 28.285-28.284-24.042-24.041-52.325 52.324ZM1147.25-46.154l-80.18-80.179 32.52-32.526 47.66 47.651 24.04-24.041-84.86-84.853 32.53-32.528 117.38 117.381-89.09 89.095Zm-71.7-80.179 71.7 71.695 80.61-80.611-108.9-108.895-24.04 24.042 84.85 84.853-32.52 32.526-47.66-47.652-24.04 24.042ZM211.665 281.188 94.286 163.809l89.095-89.097 60.811 60.812-32.526 32.526-28.284-28.284-24.042 24.042 84.853 84.854-32.528 32.526ZM102.771 163.809l108.894 108.895 24.042-24.041-84.853-84.854 32.526-32.526 28.284 28.284 24.042-24.042-52.326-52.326-80.609 80.61ZM544.945 1064.28l-32.526-32.53L681.38 862.787l42.427 42.426 35.356-35.355 32.526 32.527-67.882 67.881-42.427-42.426-136.435 136.44Zm-24.042-32.53 24.042 24.04 136.434-136.435 42.427 42.426 59.398-59.396-24.042-24.043-35.356 35.355-42.427-42.426-160.476 160.479ZM1146.48 544.547l-42.43-42.427-35.35 35.354-32.53-32.525 67.89-67.882 42.42 42.428 88.11-88.108 32.53 32.525-120.64 120.635Zm-42.42-50.912 42.42 42.428 112.15-112.15-24.04-24.041-88.11 88.108-42.42-42.428-59.4 59.398 24.04 24.041 35.36-35.356Z"></path>
                            <path fill="url(#leaderboard_intro_bg_svg__a)" fill-opacity="0.16" d="M899.108 595.471 795.725 492.089l-35.355 35.354-32.526-32.526 67.882-67.882 103.383 103.384L1027.53 402l32.53 32.526-160.952 160.945ZM795.726 483.604l103.383 103.384 152.461-152.462-24.04-24.042-128.421 128.419-103.383-103.385-59.397 59.399 24.042 24.041 35.355-35.354ZM523.848 631.654 407.39 515.197l54.446-54.447-49.184-49.184 32.526-32.527 81.711 81.711-54.447 54.447 83.933 83.932-32.527 32.525ZM415.874 515.197 523.848 623.17l24.041-24.041-83.931-83.932 54.447-54.447-73.226-73.227-24.042 24.043 49.184 49.184-54.447 54.447ZM1409.02 191.203l-32.53-32.526 176.28-176.278 58.21 58.205 42.8-42.8 32.52 32.526-75.32 75.327-58.21-58.204-143.75 143.75Zm-24.04-32.526 24.04 24.042 143.75-143.753 58.21 58.207 66.84-66.843-24.04-24.042-42.8 42.8-58.21-58.204-167.79 167.793ZM1865.02-157.604l-61.97-61.967 42.43-42.426-17.1-17.103 32.52-32.526 49.63 49.629-42.42 42.426 29.44 29.441-32.53 32.526Zm-53.48-61.966 53.48 53.482 24.04-24.041-29.44-29.441 42.43-42.426-41.15-41.144-24.04 24.041 17.1 17.103-42.42 42.426ZM954.835-666.833l-98.995-98.995-63.64 63.64-32.526-32.526 96.166-96.166 98.995 98.995 63.645-63.641 32.52 32.527-96.165 96.166Zm-98.995-107.48 98.995 98.995L1042.52-763l-24.04-24.042-63.645 63.64-98.995-98.994-87.682 87.682 24.042 24.041 63.64-63.64ZM629.1 1339.76l-32.526-32.52 117.379-117.38 32.526 32.53L629.1 1339.76Zm-24.042-32.52 24.042 24.04 108.895-108.9-24.042-24.04-108.895 108.9ZM1142.1 1379.04l-32.53-32.53 37.33-37.32-24.05-24.04-56.56 56.57-32.53-32.53 89.1-89.09 56.56 56.56 24.04-24.04-98.99-98.99 32.53-32.53 98.99 99 24.04-24.04-56.57-56.57 32.53-32.53 89.1 89.1-28.29 28.28 52.33 52.33 42.42-42.43 32.53 32.53-74.95 74.95-84.86-84.85-24.04 24.04 56.57 56.57-32.53 32.52-56.56-56.57-65.61 65.61Zm-24.04-32.53 24.04 24.05 65.61-65.61 56.56 56.57 24.05-24.04-56.57-56.57 32.52-32.53 84.86 84.86 66.47-66.47-24.05-24.05-42.42 42.43-60.81-60.81 28.28-28.28-80.61-80.61-24.04 24.04 56.57 56.57-32.53 32.52-99-98.99-24.04 24.04 99 98.99-32.53 32.53-56.57-56.57-80.61 80.61 24.05 24.05 56.56-56.57 32.53 32.52-37.32 37.32ZM324.756 1410l-131.522-131.52 98.994-98.99 67.882 67.88-32.526 32.52-35.355-35.35-33.941 33.94 66.469 66.47 50.005-50.01 32.526 32.53-82.532 82.53Zm-123.037-131.52 123.037 123.04 74.047-74.05-24.042-24.04-50.005 50-74.953-74.95 42.426-42.43 35.355 35.36 24.042-24.04-59.397-59.4-90.51 90.51ZM487.678 1360.14l-32.526-32.53 84.853-84.85-24.042-24.04-84.853 84.85-32.526-32.53 117.379-117.38 89.096 89.1-117.381 117.38Zm-24.042-32.53 24.042 24.04 108.895-108.89-80.611-80.61-108.895 108.89 24.042 24.05 84.853-84.86 32.526 32.53-84.852 84.85ZM131.725 1342.98 31.747 1243l99.978-99.98 32.527 32.53L96.8 1243l67.452 67.45-32.527 32.53ZM40.231 1243l91.493 91.49 24.043-24.04L88.315 1243l67.452-67.45-24.043-24.04L40.231 1243ZM954.835 1333.17l-98.995-99-63.64 63.64-32.526-32.52 96.166-96.17 98.995 98.99 63.645-63.63L1051 1237l-96.165 96.17Zm-98.995-107.48 98.995 98.99 87.685-87.68-24.04-24.04-63.645 63.64-98.995-99-87.682 87.69 24.042 24.04 63.64-63.64ZM1960.55 508.172l-32.53-32.527 117.38-117.379 32.53 32.527-117.38 117.379Zm-24.05-32.527 24.05 24.043 108.89-108.895-24.04-24.043-108.9 108.895Z"></path>
                        </clipPath>
                    </defs>
                    <ellipse cx="0%" cy="24.69135802469136%" fill="url(#leaderboard_intro_bg_svg__b)" clip-path="url(#leaderboard_intro_bg_svg__c)" rx="450" ry="300"></ellipse>
                    <circle cx="50%" cy="50%" r="5000" fill="url(#leaderboard_intro_bg_svg__d)" clip-path="url(#leaderboard_intro_bg_svg__c)"></circle>
                </svg>
                <Paper elevation={3} className="admin-paper" sx={{ width: '90%', margin: '0 auto' }}>
                    <div className="admin-header">
                        <div className="admin-header-left">
                            <div className="admin-header-icon">
                                <FiFileText />
                            </div>
                            <div>
                                <h1 className="admin-header-title">Solicitudes de Tipeo</h1>
                                <p className="admin-header-subtitle">
                                    Faltan: {solicitudes?.filter((sorteo) => sorteo.status === 'pending').filter(filterSolicitud).length} solicitudes por revisar | {solicitudes?.filter((sorteo) => sorteo.status === 'pending').filter(filterSolicitud).filter(s => s.bnbAddress && bnbNuevasSet.has(s.bnbAddress.trim().toLowerCase())).length} direcciones bnb nuevas
                                </p>
                            </div>
                        </div>
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                disabled={loading || solicitudes?.filter((sorteo) => sorteo.status === 'pending').filter(filterSolicitud).length === 0}
                                onClick={handleAbrirModalBulkAccept}
                                startIcon={<FiCheckCircle />}
                                sx={{
                                    backgroundColor: '#10b981',
                                    color: '#fff',
                                    fontWeight: 'bold',
                                    '&:hover': { backgroundColor: '#059669' },
                                    '&.Mui-disabled': { backgroundColor: 'rgba(16, 185, 129, 0.4)', color: 'rgba(255, 255, 255, 0.5)' }
                                }}
                            >
                                Aceptar Todas ({solicitudes?.filter((sorteo) => sorteo.status === 'pending').filter(filterSolicitud).length})
                            </Button>
                            <Button
                                variant="outlined"
                                className="admin-download-btn"
                                disabled={loading}
                                onClick={handleDescargarBNB}
                            >
                                <FiDownload /> descargar direcciones bnb
                            </Button>
                        </Box>
                    </div>

                    <Tabs
                        value={tabValue}
                        onChange={(e, newValue) => setTabValue(newValue)}
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        <Tab label="1XBET" />
                        {/* <Tab label="EXTRA 1XBET" /> */}
                        <Tab label="VERIFICAR 1XBET" />
                        <Tab label="APELACIONES BLACKLIST" sx={{ color: '#ef4444 !important', fontWeight: 'bold' }} />
                    </Tabs>

                    {error && (
                        <Box sx={{ p: 2 }}>
                            <Alert severity="error">
                                {error}
                            </Alert>
                        </Box>
                    )}

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
                            <CircularProgress size={50} />
                        </Box>
                    ) : solicitudes.filter(s => s.status === 'pending').filter(filterSolicitud).length === 0 ? (
                        <Typography className="admin-empty" align="center">No hay solicitudes disponibles.</Typography>
                    ) : (
                        <TableContainer className="admin-table-wrap">
                            <Table className="admin-table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Fecha</TableCell>
                                        <TableCell>Nombre</TableCell>
                                        <TableCell>Telegram ID</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>{(tabValue === 2 || tabValue === 3) ? 'Captura / Prueba' : (tabValue === 1 || tabValue === 4 || tabValue === 5 ? 'Captura Depósito' : 'Captura Datos')}</TableCell>
                                        {(tabValue === 2 || tabValue === 3) ? (
                                            <TableCell>Mensaje</TableCell>
                                        ) : (
                                            tabValue !== 5 && (
                                                <TableCell>
                                                    Dirección BNB
                                                </TableCell>
                                            )
                                        )}
                                        {tabValue !== 2 && tabValue !== 5 && tabValue !== 1 && tabValue !== 4 && tabValue !== 3 && <TableCell>FingerPrint</TableCell>}
                                        <TableCell>Acciones</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {solicitudes?.filter((sorteo) => sorteo.status === 'pending').filter(filterSolicitud).reverse()?.map((solicitud) => {
                                        const isBnbNueva = solicitud.bnbAddress && bnbNuevasSet.has(solicitud.bnbAddress.trim().toLowerCase());
                                        const isBnbLowerCase = solicitud.bnbAddress && solicitud.bnbAddress === solicitud.bnbAddress.toLowerCase();
                                        const bnbKey = solicitud.bnbAddress ? solicitud.bnbAddress.trim().toLowerCase() : '';
                                        const infoAsociados = bnbKey ? bnbAsociadosMap[bnbKey] : null;
                                        return (
                                            <TableRow key={solicitud.id}>
                                                <TableCell>
                                                    <div className="admin-date-wrap">
                                                        <span className="admin-date-icon" style={{ color: '#3b82f6' }}><FiCalendar /></span>
                                                        <span className="admin-date-text">
                                                            {formatearFecha(solicitud.createdAt)}
                                                            <br />
                                                            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem' }}>
                                                                {solicitud.createdAt.substring(11, 16)}
                                                            </span>
                                                        </span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="admin-name-wrap" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <div className="admin-avatar">
                                                                <FiUser />
                                                            </div>
                                                            <span className="admin-name-text">{solicitud.nombre}</span>
                                                        </div>
                                                        {(solicitud.xbet_id || verificacionXbetId[solicitud.id]?.expectedId) && (
                                                            <span
                                                                title={`ID 1XBET registrado por el usuario: ${solicitud.xbet_id || verificacionXbetId[solicitud.id]?.expectedId}`}
                                                                style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                    background: 'rgba(59, 130, 246, 0.12)',
                                                                    border: '1px solid rgba(59, 130, 246, 0.3)',
                                                                    borderRadius: '4px',
                                                                    padding: '1px 6px',
                                                                    fontSize: '0.71rem',
                                                                    color: '#60a5fa',
                                                                    fontWeight: '600'
                                                                }}
                                                            >
                                                                🆔 1XBET: {solicitud.xbet_id || verificacionXbetId[solicitud.id]?.expectedId}
                                                            </span>
                                                        )}
                                                        {canjesBlacklistSet.has((solicitud.nombre || '').trim().toLowerCase()) && (
                                                            <span
                                                                title="El usuario canjeó en la tienda la opción de Salir de Blacklist (Canje #4)"
                                                                style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                    background: 'rgba(16, 185, 129, 0.2)',
                                                                    border: '1px solid #10b981',
                                                                    borderRadius: '6px',
                                                                    padding: '2px 7px',
                                                                    fontSize: '0.72rem',
                                                                    color: '#34d399',
                                                                    fontWeight: 'bold',
                                                                    letterSpacing: '0.02em',
                                                                }}
                                                            >
                                                                🎟️ CANJEÓ SALIR DE BLACKLIST
                                                            </span>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <FaTelegramPlane style={{ color: '#0088cc', fontSize: '0.95rem', flexShrink: 0 }} />
                                                        <span style={{
                                                            fontFamily: (solicitud.telegramId || solicitud.telegram_id || solicitud.telegram || solicitud.tgId) ? 'monospace' : 'inherit',
                                                            fontWeight: (solicitud.telegramId || solicitud.telegram_id || solicitud.telegram || solicitud.tgId) ? '700' : 'normal',
                                                            color: (solicitud.telegramId || solicitud.telegram_id || solicitud.telegram || solicitud.tgId) ? '#38bdf8' : 'rgba(255,255,255,0.4)',
                                                            fontSize: '0.85rem'
                                                        }}>
                                                            {solicitud.telegramId || solicitud.telegram_id || solicitud.telegram || solicitud.tgId || '-'}
                                                        </span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span style={{ color: '#3b82f6', display: 'flex', fontSize: '0.95rem' }}><FiMail /></span>
                                                        <span>{solicitud.email}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                                        <Button
                                                            className="admin-capture-link"
                                                            onClick={() => handleOpenScreenshot(
                                                                tabValue === 0 ? solicitud.offerScreenshot : ((tabValue === 2 || tabValue === 3) ? (solicitud.offerScreenshot || solicitud.bnbScreenshot) : solicitud.bnbScreenshot),
                                                                solicitud.id
                                                            )}
                                                            startIcon={<FiImage />}
                                                        >
                                                            Ver Captura
                                                        </Button>
                                                        {verificacionIA[solicitud.id]?.loading && (
                                                            <span style={{ fontSize: '0.72rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                <CircularProgress size={10} color="inherit" /> Analizando IA...
                                                            </span>
                                                        )}
                                                        {verificacionIA[solicitud.id]?.isAI && (
                                                            <span
                                                                title={verificacionIA[solicitud.id]?.reason || 'IA Detectada'}
                                                                style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                    background: 'rgba(239, 68, 68, 0.18)',
                                                                    border: '1px solid #ef4444',
                                                                    borderRadius: '6px',
                                                                    padding: '2px 7px',
                                                                    fontSize: '0.72rem',
                                                                    color: '#f87171',
                                                                    fontWeight: 'bold',
                                                                    letterSpacing: '0.02em',
                                                                }}
                                                            >
                                                                🤖 IA DETECTADA
                                                            </span>
                                                        )}
                                                        {verificacionFecha[solicitud.id]?.difiere && (
                                                            <span
                                                                title={`Fecha en captura (${verificacionFecha[solicitud.id]?.fecha}) no coincide con la fecha de la solicitud (${verificacionFecha[solicitud.id]?.fechaSolicitud})`}
                                                                style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                    background: 'rgba(239, 68, 68, 0.18)',
                                                                    border: '1px solid #ef4444',
                                                                    borderRadius: '6px',
                                                                    padding: '2px 7px',
                                                                    fontSize: '0.72rem',
                                                                    color: '#f87171',
                                                                    fontWeight: 'bold',
                                                                    letterSpacing: '0.02em',
                                                                }}
                                                            >
                                                                ⚠️ FECHA DIFIERE (Imagen: {verificacionFecha[solicitud.id]?.fecha} vs Solicitud: {verificacionFecha[solicitud.id]?.fechaSolicitud})
                                                            </span>
                                                        )}
                                                        {verificacionFecha[solicitud.id]?.coincide && (
                                                            <span
                                                                title={`Fecha en captura (${verificacionFecha[solicitud.id]?.fecha}) coincide con la fecha de la solicitud`}
                                                                style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                    background: 'rgba(16, 185, 129, 0.18)',
                                                                    border: '1px solid #10b981',
                                                                    borderRadius: '6px',
                                                                    padding: '2px 7px',
                                                                    fontSize: '0.72rem',
                                                                    color: '#34d399',
                                                                    fontWeight: 'bold',
                                                                    letterSpacing: '0.02em',
                                                                }}
                                                            >
                                                                ✅ FECHA COINCIDE ({verificacionFecha[solicitud.id]?.fecha})
                                                            </span>
                                                        )}
                                                        {verificacionFecha[solicitud.id]?.esMenor && !verificacionFecha[solicitud.id]?.difiere && !verificacionFecha[solicitud.id]?.coincide && (
                                                            <span
                                                                title={`Fecha detectada en la captura (${verificacionFecha[solicitud.id]?.fecha}) es anterior al 6 de junio de 2026`}
                                                                style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                    background: 'rgba(245, 158, 11, 0.18)',
                                                                    border: '1px solid #f59e0b',
                                                                    borderRadius: '6px',
                                                                    padding: '2px 7px',
                                                                    fontSize: '0.72rem',
                                                                    color: '#fbbf24',
                                                                    fontWeight: 'bold',
                                                                    letterSpacing: '0.02em',
                                                                }}
                                                            >
                                                                📅 FECHA ANTERIOR AL 6/6/2026 ({verificacionFecha[solicitud.id]?.fecha})
                                                            </span>
                                                        )}
                                                        {verificacionIA[solicitud.id] && !verificacionIA[solicitud.id].loading && !verificacionIA[solicitud.id].isAI && (
                                                            <span style={{ fontSize: '0.7rem', color: '#10b981', fontWeight: '500' }}>
                                                                ✓ Sin marcas de IA
                                                            </span>
                                                        )}
                                                        {/* Verificación de ID 1XBET vs Captura */}
                                                        {verificacionXbetId[solicitud.id]?.coincide && (
                                                            <span
                                                                title={`ID 1XBET verificado: coincide con la cuenta en la captura (${verificacionXbetId[solicitud.id]?.expectedId})`}
                                                                style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                    background: 'rgba(16, 185, 129, 0.18)',
                                                                    border: '1px solid #10b981',
                                                                    borderRadius: '6px',
                                                                    padding: '2px 7px',
                                                                    fontSize: '0.72rem',
                                                                    color: '#34d399',
                                                                    fontWeight: 'bold',
                                                                    letterSpacing: '0.02em',
                                                                }}
                                                            >
                                                                ✅ ID 1XBET COINCIDE ({verificacionXbetId[solicitud.id]?.expectedId})
                                                            </span>
                                                        )}
                                                        {verificacionXbetId[solicitud.id]?.difiere && (
                                                            <span
                                                                title={`ID 1XBET difiere: en captura se lee ${verificacionXbetId[solicitud.id]?.detectedId} y el usuario registró ${verificacionXbetId[solicitud.id]?.expectedId}`}
                                                                style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                    background: 'rgba(239, 68, 68, 0.18)',
                                                                    border: '1px solid #ef4444',
                                                                    borderRadius: '6px',
                                                                    padding: '2px 7px',
                                                                    fontSize: '0.72rem',
                                                                    color: '#f87171',
                                                                    fontWeight: 'bold',
                                                                    letterSpacing: '0.02em',
                                                                }}
                                                            >
                                                                ⚠️ ID 1XBET DIFIERE (Img: {verificacionXbetId[solicitud.id]?.detectedId} vs User: {verificacionXbetId[solicitud.id]?.expectedId})
                                                            </span>
                                                        )}
                                                        {verificacionXbetId[solicitud.id]?.noDetectado && (
                                                            <span
                                                                title={`No se detectó el número de cuenta en la captura (Usuario: ${verificacionXbetId[solicitud.id]?.expectedId})`}
                                                                style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                    background: 'rgba(245, 158, 11, 0.18)',
                                                                    border: '1px solid #f59e0b',
                                                                    borderRadius: '6px',
                                                                    padding: '2px 7px',
                                                                    fontSize: '0.72rem',
                                                                    color: '#fbbf24',
                                                                    fontWeight: 'bold',
                                                                    letterSpacing: '0.02em',
                                                                }}
                                                            >
                                                                🔍 ID 1XBET ({verificacionXbetId[solicitud.id]?.expectedId}) NO VISIBLE
                                                            </span>
                                                        )}
                                                        {verificacionXbetId[solicitud.id]?.sinId && (
                                                            <span
                                                                title="El usuario no tiene registrado su ID de 1XBET"
                                                                style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                    background: 'rgba(239, 68, 68, 0.14)',
                                                                    border: '1px solid rgba(239, 68, 68, 0.4)',
                                                                    borderRadius: '6px',
                                                                    padding: '2px 7px',
                                                                    fontSize: '0.72rem',
                                                                    color: '#fca5a5',
                                                                    fontWeight: 'bold',
                                                                    letterSpacing: '0.02em',
                                                                }}
                                                            >
                                                                ⚠️ SIN ID 1XBET REGISTRADO
                                                            </span>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                {(tabValue === 2 || tabValue === 3) ? (
                                                    <TableCell sx={{ maxWidth: 320 }}>
                                                        <div style={{
                                                            backgroundColor: 'rgba(239, 68, 68, 0.08)',
                                                            border: '1px solid rgba(239, 68, 68, 0.2)',
                                                            borderRadius: '10px',
                                                            padding: '8px 12px'
                                                        }}>
                                                            <span style={{ fontSize: '0.72rem', color: '#ef4444', fontWeight: 'bold', display: 'block', marginBottom: '3px' }}>
                                                                MENSAJE:
                                                            </span>
                                                            <span style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.4, wordBreak: 'break-word' }}>
                                                                {solicitud.motivo || solicitud.mensaje || 'Sin motivo expresado'}
                                                            </span>
                                                        </div>
                                                    </TableCell>
                                                ) : (
                                                    tabValue !== 5 && (
                                                        <TableCell>
                                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                    <span style={{ color: isBnbLowerCase ? '#f97316' : (isBnbNueva ? '#10b981' : '#ef4444'), display: 'flex', fontSize: '0.95rem' }}><FiCopy /></span>
                                                                    <span className="admin-bnb-address" style={{
                                                                        color: isBnbLowerCase ? '#f97316' : (isBnbNueva ? '#10b981' : '#ef4444'),
                                                                        fontWeight: isBnbNueva ? 'bold' : 'normal',
                                                                        textDecoration: isBnbLowerCase ? 'underline' : 'none'
                                                                    }}>
                                                                        {solicitud.bnbAddress}
                                                                    </span>
                                                                </div>
                                                                {infoAsociados && (infoAsociados.cantidadUsuarios > 1 || (infoAsociados.usuarios && infoAsociados.usuarios.length > 1)) && (
                                                                    <span
                                                                        title={`Usuarios con esta dirección BNB: ${infoAsociados.usuarios ? infoAsociados.usuarios.join(', ') : ''}`}
                                                                        style={{
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            gap: '4px',
                                                                            background: 'rgba(239, 68, 68, 0.18)',
                                                                            border: '1px solid #ef4444',
                                                                            borderRadius: '6px',
                                                                            padding: '2px 7px',
                                                                            fontSize: '0.72rem',
                                                                            color: '#f87171',
                                                                            fontWeight: 'bold',
                                                                            letterSpacing: '0.02em',
                                                                            maxWidth: '280px',
                                                                            flexWrap: 'wrap',
                                                                            wordBreak: 'break-word'
                                                                        }}
                                                                    >
                                                                        ⚠️ {infoAsociados.cantidadUsuarios || infoAsociados.usuarios?.length} usuarios en esta BNB: {infoAsociados.usuarios ? infoAsociados.usuarios.join(', ') : ''}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                    )
                                                )}
                                                {tabValue !== 2 && tabValue !== 5 && tabValue !== 1 && tabValue !== 4 && tabValue !== 3 && (
                                                    <TableCell>
                                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                                            <div>
                                                                {/* Fingerprint hash */}
                                                                <span style={{
                                                                    color: verificacionFingerprint[solicitud.id] === 'duplicate'
                                                                        ? '#ff4444'
                                                                        // : verificacionFingerprint[solicitud.id] === 'name_dup'
                                                                        //     ? '#ff9800'
                                                                        : verificacionFingerprint[solicitud.id] === 'hist_name_dup'
                                                                            ? '#ffb347'
                                                                            : verificacionFingerprint[solicitud.id] === 'hist_bnb_dup'
                                                                                ? '#60a5fa'
                                                                                : '#44ff88',
                                                                    fontWeight: ['duplicate', 'name_dup', 'hist_name_dup', 'hist_bnb_dup'].includes(verificacionFingerprint[solicitud.id]) ? 'bold' : 'normal',
                                                                    fontFamily: 'monospace',
                                                                    fontSize: '0.8rem'
                                                                }}>
                                                                    {solicitud.fingerprint
                                                                        ? solicitud.fingerprint.substring(0, 16) + '…'
                                                                        : '-'}
                                                                </span>

                                                                {/* Badge: VPN detectado directamente en la solicitud */}
                                                                {(solicitud.vpn_suspected === true || solicitud.vpn_suspected === 1 || solicitud.vpn_suspected >= 50) && (
                                                                    <span
                                                                        title={Array.isArray(solicitud.vpnReasons) ? solicitud.vpnReasons.join(', ') : 'VPN Detectada'}
                                                                        style={{
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            gap: '4px',
                                                                            marginTop: '4px',
                                                                            marginLeft: '2px',
                                                                            background: 'rgba(168,85,247,0.18)',
                                                                            border: '1px solid #a855f7',
                                                                            borderRadius: '6px',
                                                                            padding: '2px 7px',
                                                                            fontSize: '0.72rem',
                                                                            color: '#c084fc',
                                                                            fontWeight: 'bold',
                                                                            letterSpacing: '0.02em',
                                                                        }}
                                                                    >
                                                                        🟣 VPN DETECTADA {solicitud.vpnRiskScore ? `(${solicitud.vpnRiskScore}%)` : ''}
                                                                    </span>
                                                                )}

                                                                {/* Badge: Multicuenta exacta (mismo FP) */}
                                                                {verificacionFingerprint[solicitud.id] === 'duplicate' && (
                                                                    <span style={{
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        gap: '4px',
                                                                        marginTop: '4px',
                                                                        marginLeft: '2px',
                                                                        background: 'rgba(255,68,68,0.15)',
                                                                        border: '1px solid #ff4444',
                                                                        borderRadius: '6px',
                                                                        padding: '2px 7px',
                                                                        fontSize: '0.72rem',
                                                                        color: '#ff4444',
                                                                        fontWeight: 'bold',
                                                                        letterSpacing: '0.02em',
                                                                    }}>
                                                                        🔴 MULTICUENTA
                                                                        {infoMulticuenta[solicitud.id] && (
                                                                            <span style={{ fontWeight: 'normal', color: '#ffaaaa' }}>
                                                                                &nbsp;con: {infoMulticuenta[solicitud.id]}
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                )}

                                                                {/* Badge: Mismo nombre, FP distinto → sospecha VPN */}
                                                                {/* {verificacionFingerprint[solicitud.id] === 'name_dup' && (
                                                                    <span style={{
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        gap: '4px',
                                                                        marginTop: '4px',
                                                                        marginLeft: '2px',
                                                                        background: 'rgba(255,152,0,0.15)',
                                                                        border: '1px solid #ff9800',
                                                                        borderRadius: '6px',
                                                                        padding: '2px 7px',
                                                                        fontSize: '0.72rem',
                                                                        color: '#ff9800',
                                                                        fontWeight: 'bold',
                                                                        letterSpacing: '0.02em',
                                                                        maxWidth: '280px',
                                                                        flexWrap: 'wrap',
                                                                        wordBreak: 'break-word',
                                                                    }}>
                                                                        🟠 MISMO NOMBRE / VPN?
                                                                        {infoMulticuenta[solicitud.id] && (
                                                                            <span style={{ fontWeight: 'normal', color: '#ffcc80', display: 'block', fontSize: '0.68rem', marginTop: '2px' }}>
                                                                                También visto como: {infoMulticuenta[solicitud.id]}
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                )} */}

                                                                {/* Badge: Único */}
                                                                {verificacionFingerprint[solicitud.id] === 'unique' && (
                                                                    <span style={{
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        gap: '4px',
                                                                        marginTop: '4px',
                                                                        marginLeft: '2px',
                                                                        background: 'rgba(68,255,136,0.10)',
                                                                        border: '1px solid #44ff88',
                                                                        borderRadius: '6px',
                                                                        padding: '2px 7px',
                                                                        fontSize: '0.72rem',
                                                                        color: '#44ff88',
                                                                        fontWeight: 'bold',
                                                                    }}>
                                                                        ✓ Único
                                                                    </span>
                                                                )}

                                                                {/* Badge: Mismo nombre en historial / VPN histórico */}
                                                                {/* {verificacionFingerprint[solicitud.id] === 'hist_name_dup' && (
                                                                    <span style={{
                                                                        display: 'inline-flex',
                                                                        alignItems: 'flex-start',
                                                                        flexDirection: 'column',
                                                                        gap: '2px',
                                                                        marginTop: '4px',
                                                                        marginLeft: '2px',
                                                                        background: 'rgba(255,179,71,0.12)',
                                                                        border: '1px solid #ffb347',
                                                                        borderRadius: '6px',
                                                                        padding: '3px 7px',
                                                                        fontSize: '0.72rem',
                                                                        color: '#ffb347',
                                                                        fontWeight: 'bold',
                                                                        letterSpacing: '0.02em',
                                                                        maxWidth: '300px',
                                                                        wordBreak: 'break-word',
                                                                    }}>
                                                                        🟡 YA COBRÓ ANTES (VPN)
                                                                        {infoMulticuenta[solicitud.id] && (
                                                                            <span style={{ fontWeight: 'normal', color: '#ffd580', fontSize: '0.67rem', marginTop: '1px' }}>
                                                                                {infoMulticuenta[solicitud.id]}
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                )} */}

                                                                {/* Badge: BNB ya usada en historial */}
                                                                {verificacionFingerprint[solicitud.id] === 'hist_bnb_dup' && (
                                                                    <span style={{
                                                                        display: 'inline-flex',
                                                                        alignItems: 'flex-start',
                                                                        flexDirection: 'column',
                                                                        gap: '2px',
                                                                        marginTop: '4px',
                                                                        marginLeft: '2px',
                                                                        background: 'rgba(96,165,250,0.12)',
                                                                        border: '1px solid #60a5fa',
                                                                        borderRadius: '6px',
                                                                        padding: '3px 7px',
                                                                        fontSize: '0.72rem',
                                                                        color: '#60a5fa',
                                                                        fontWeight: 'bold',
                                                                        letterSpacing: '0.02em',
                                                                        maxWidth: '300px',
                                                                        wordBreak: 'break-word',
                                                                    }}>
                                                                        🔵 BNB YA USADA
                                                                        {infoMulticuenta[solicitud.id] && (
                                                                            <span style={{ fontWeight: 'normal', color: '#93c5fd', fontSize: '0.67rem', marginTop: '1px' }}>
                                                                                {infoMulticuenta[solicitud.id]}
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                )}
                                                <TableCell>
                                                    {solicitud.status === 'pending' && (
                                                        <div className="admin-actions-wrap">
                                                            <Button
                                                                variant="outlined"
                                                                className="admin-btn-accept"
                                                                startIcon={<FiCheckCircle />}
                                                                onClick={() =>
                                                                    handleAction(
                                                                        solicitud.id,
                                                                        'accepted',
                                                                        '',
                                                                        solicitud.nombre,
                                                                        solicitud.tipo
                                                                    )
                                                                }
                                                                disabled={loading}
                                                            >
                                                                Aceptar
                                                            </Button>
                                                            <Button
                                                                variant="outlined"
                                                                className="admin-btn-reject"
                                                                startIcon={<FiXCircle />}
                                                                onClick={() => handleAbrirModalRechazo(solicitud)}
                                                                disabled={loading}
                                                            >
                                                                Rechazar
                                                            </Button>

                                                            <Button
                                                                variant="outlined"
                                                                className="admin-btn-delete"
                                                                startIcon={<FiEye />}
                                                                onClick={() =>
                                                                    handleAction(
                                                                        solicitud.id,
                                                                        'vetado',
                                                                        '',
                                                                        solicitud.nombre,
                                                                        solicitud.tipo
                                                                    )
                                                                }
                                                                disabled={loading}
                                                            >
                                                                Eliminar
                                                            </Button>

                                                        </div>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    <div className="admin-footer">
                        <div className="admin-footer-text">
                            <FiInfo style={{ fontSize: '1rem', color: '#3b82f6' }} />
                            <span>Mostrando {solicitudes?.filter((sorteo) => sorteo.status === 'pending').filter(filterSolicitud).length} solicitudes pendientes de revisión</span>
                        </div>

                    </div>
                </Paper>

                {/* Diálogo para mostrar capturas */}
                <Dialog open={openDialog} maxWidth="md" fullWidth>
                    <DialogTitle>Visualizar Captura</DialogTitle>
                    <DialogContent>
                        {selectedSolicitudId && verificacionIA[selectedSolicitudId]?.isAI && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                <strong>🤖 ¡Atención! Captura sospechosa de haber sido generada o modificada con IA.</strong>
                                <br />
                                {verificacionIA[selectedSolicitudId]?.reason}
                            </Alert>
                        )}
                        {selectedSolicitudId && verificacionXbetId[selectedSolicitudId]?.difiere && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                <strong>⚠️ ¡Atención! El ID de 1XBET en la captura no coincide con el del usuario.</strong>
                                <br />
                                ID detectado en imagen: <strong>{verificacionXbetId[selectedSolicitudId]?.detectedId}</strong> vs ID registrado por el usuario: <strong>{verificacionXbetId[selectedSolicitudId]?.expectedId}</strong>
                            </Alert>
                        )}
                        {selectedSolicitudId && verificacionXbetId[selectedSolicitudId]?.coincide && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                <strong>✅ ID 1XBET Verificado:</strong> Coincide el ID de cuenta de la imagen con el registrado por el usuario (<strong>{verificacionXbetId[selectedSolicitudId]?.expectedId}</strong>).
                            </Alert>
                        )}
                        {selectedSolicitudId && verificacionXbetId[selectedSolicitudId]?.noDetectado && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <strong>🔍 ID 1XBET ({verificacionXbetId[selectedSolicitudId]?.expectedId}) no visible:</strong> No se pudo detectar claramente el número de cuenta en la imagen. Verificalo manualmente.
                            </Alert>
                        )}
                        {selectedSolicitudId && verificacionXbetId[selectedSolicitudId]?.sinId && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <strong>ℹ️ Sin ID 1XBET:</strong> El usuario no tiene ningún ID de 1XBET registrado en su perfil.
                            </Alert>
                        )}
                        {selectedSolicitudId && verificacionFecha[selectedSolicitudId]?.esMenor && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <strong>📅 ¡Atención! Se detectó una fecha ({verificacionFecha[selectedSolicitudId]?.fecha}) anterior al 6 de junio de 2026 en la captura.</strong>
                            </Alert>
                        )}
                        {selectedSolicitudId && verificacionIA[selectedSolicitudId] && !verificacionIA[selectedSolicitudId].loading && !verificacionIA[selectedSolicitudId].isAI && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                ✓ No se detectaron firmas ni marcas de agua de IA en la captura.
                            </Alert>
                        )}
                        {selectedScreenshot ? (
                            <CapturaImagen src={selectedScreenshot} />
                        ) : (
                            <Alert severity="warning">
                                ⚠️ No hay captura disponible para esta solicitud.
                            </Alert>
                        )}
                    </DialogContent>
                    <DialogActions>
                        {selectedScreenshot && (
                            <Button
                                onClick={() => window.open(selectedScreenshot, '_blank')}
                                variant="text"
                                color="primary"
                            >
                                🔗 Abrir en nueva pestaña
                            </Button>
                        )}
                        <Button onClick={handleCloseDialog} variant="outlined">
                            Cerrar
                        </Button>
                    </DialogActions>
                </Dialog>


                <Dialog open={openBnb} maxWidth="md" fullWidth>
                    <DialogTitle>Visualizar Dirección BNB</DialogTitle>
                    <DialogContent>
                        <Typography>{bnb}</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseAdrres} variant="outlined">
                            Cerrar
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={openRazon} onClose={() => setOpenRazon(false)}>
                    <DialogTitle>Indicar razón del rechazo</DialogTitle>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <TextField
                            multiline
                            fullWidth
                            rows={4}
                            value={razonRechazo}
                            onChange={(e) => setRazonRechazo(e.target.value)}
                            placeholder="Escribí la razón del rechazo..."
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={agregarBlacklist}
                                    onChange={(e) => setAgregarBlacklist(e.target.checked)}
                                    sx={{
                                        color: '#ff5252',
                                        '&.Mui-checked': {
                                            color: '#ff5252',
                                        },
                                    }}
                                />
                            }
                            label="Agregar usuario a la blacklist"
                            sx={{ color: 'black', fontFamily: 'Outfit' }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenRazon(false)} color="inherit">
                            Cancelar
                        </Button>
                        <Button
                            onClick={async () => {
                                if (agregarBlacklist && solicitudRechazadaId?.nombre) {
                                    try {
                                        await axios.post(
                                            'https://backend.eldenguee.com/sorteo/blacklist/agregar',
                                            { nombre: solicitudRechazadaId.nombre, motivo: razonRechazo, },
                                            {
                                                headers: {
                                                    "x-internal-api-key": process.env.REACT_APP_PANEL_API_KEY || process.env.REACT_APP_INTERNAL_API_KEY || 'ed_qatar_2022_messi_dengueclarasoluna_2026',
                                                    "x-api-key": process.env.REACT_APP_PANEL_API_KEY || process.env.REACT_APP_INTERNAL_API_KEY || 'ed_qatar_2022_messi_dengueclarasoluna_2026'
                                                }
                                            }
                                        );
                                    } catch (err) {
                                        console.error('Error al agregar a la blacklist:', err);
                                    }
                                }
                                await handleAction(
                                    solicitudRechazadaId?.id,
                                    'rejected',
                                    razonRechazo,
                                    solicitudRechazadaId?.nombre,
                                    solicitudRechazadaId?.tipo
                                );
                                setOpenRazon(false);
                                setRazonRechazo('');
                                setAgregarBlacklist(false);
                            }}
                            color="error"
                            variant="contained"
                        >
                            Confirmar rechazo
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Modal de Aceptación Masiva por Tab */}
                <Dialog open={openBulkAcceptModal} onClose={() => !bulkProcessing && setOpenBulkAcceptModal(false)} maxWidth="md" fullWidth>
                    <DialogTitle sx={{ fontFamily: 'Outfit', fontWeight: 'bold' }}>
                        Confirmar Aceptación Masiva ({bulkAcceptList.length})
                    </DialogTitle>
                    <DialogContent dividers sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
                        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                            A continuación se listan las solicitudes pendientes de la pestaña actual. Podés eliminar de la lista cualquier solicitud que no quieras aceptar antes de confirmar.
                        </Typography>
                        {bulkAcceptList.length === 0 ? (
                            <Alert severity="warning">No hay solicitudes seleccionadas para aceptar.</Alert>
                        ) : (
                            <List disablePadding>
                                {bulkAcceptList.map((sol) => (
                                    <ListItem
                                        key={sol.id}
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                aria-label="eliminar"
                                                color="error"
                                                onClick={() => handleRemoveFromBulkList(sol.id)}
                                                disabled={bulkProcessing}
                                                title="Quitar de la lista"
                                            >
                                                <FiTrash2 />
                                            </IconButton>
                                        }
                                        sx={{
                                            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                                            py: 1,
                                        }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                        {sol.nombre}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                                        ({sol.email})
                                                    </Typography>
                                                </Box>
                                            }
                                            secondary={
                                                <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap', fontSize: '0.8rem' }}>
                                                    {sol.bnbAddress && (
                                                        <span>
                                                            BNB: <strong style={{
                                                                color: (sol.bnbAddress === sol.bnbAddress.toLowerCase()) ? '#f97316' : '#10b981',
                                                                textDecoration: (sol.bnbAddress === sol.bnbAddress.toLowerCase()) ? 'underline' : 'none'
                                                            }}>{sol.bnbAddress}</strong>
                                                        </span>
                                                    )}
                                                    {sol.createdAt && (
                                                        <span>Fecha: {formatearFecha(sol.createdAt)}</span>
                                                    )}
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button
                            onClick={() => setOpenBulkAcceptModal(false)}
                            color="inherit"
                            disabled={bulkProcessing}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleConfirmBulkAccept}
                            color="success"
                            variant="contained"
                            disabled={bulkProcessing || bulkAcceptList.length === 0}
                            startIcon={bulkProcessing ? <CircularProgress size={18} color="inherit" /> : <FiCheck />}
                            sx={{
                                backgroundColor: '#10b981',
                                '&:hover': { backgroundColor: '#059669' },
                            }}
                        >
                            {bulkProcessing
                                ? 'Procesando...'
                                : `Confirmar y Aceptar (${bulkAcceptList.length})`}
                        </Button>
                    </DialogActions>
                </Dialog>

            </Box>
        </Nlayout>
    );
};

export default AdminPanel;